import { z } from 'zod';

export const validators = {
    activateAccountV2Request: {
        userId: z.string().min(1),
        activateCode: z.string().min(1).max(1024),
        password: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
        bodySchema: z.object({
            userId: z.string().min(1),
            activateCode: z.string().min(1).max(1024),
            password: z
                .string()
                .min(10)
                .max(256)
                .regex(/^.{10,256}$/),
        }),
    },
    activateElectricalCheckListCommand: {
        electricalCheckListId: z.string().min(1),
        bodySchema: z.object({
            electricalCheckListId: z.string().min(1),
        }),
    },
    activateElectricalInspectionNormListCommand: {
        electricalInspectionNormListId: z.string().min(1),
        bodySchema: z.object({
            electricalInspectionNormListId: z.string().min(1),
        }),
    },
    addRoleToUserCommand: {
        userId: z.string().min(1),
        bodySchema: z.object({
            userId: z.string().min(1),
        }),
    },
    changeAccountEmailV2Request: {
        email: z.union([z.literal(''), z.string().email()]),
        password: z.string().min(1).max(256),
        bodySchema: z.object({
            email: z.union([z.literal(''), z.string().email()]),
            password: z.string().min(1).max(256),
        }),
    },
    changeAccountPasswordV2Request: {
        currentPassword: z.string().min(1).max(256),
        newPassword: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
        bodySchema: z.object({
            currentPassword: z.string().min(1).max(256),
            newPassword: z
                .string()
                .min(10)
                .max(256)
                .regex(/^.{10,256}$/),
        }),
    },
    changeElectricalProjectStateCommand: {
        electricalProjectVersionStatusEnum: [
            'Request',
            'PriceQuote',
            'Planned',
            'Inspection',
            'RepeatInspection',
            'Complete',
            'Failed',
            'Order',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        status: z.enum([
            'Request',
            'PriceQuote',
            'Planned',
            'Inspection',
            'RepeatInspection',
            'Complete',
            'Failed',
            'Order',
        ]),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            status: z.enum([
                'Request',
                'PriceQuote',
                'Planned',
                'Inspection',
                'RepeatInspection',
                'Complete',
                'Failed',
                'Order',
            ]),
        }),
    },
    changeUserEmailCommand: {
        userId: z.string().min(1),
        email: z.union([z.literal(''), z.string().email()]),
        bodySchema: z.object({
            userId: z.string().min(1),
            email: z.union([z.literal(''), z.string().email()]),
        }),
    },
    copyDocumentCommand: {
        documentId: z.string().min(1),
        bodySchema: z.object({
            documentId: z.string().min(1),
        }),
    },
    copyElectricalProjectCommand: {
        electricalProjectId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
        bodySchema: z.object({
            electricalProjectId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            description: z.string().max(100).nullable().optional(),
        }),
    },
    copyElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        count: z.number().min(1).max(200),
        bodySchema: z.object({
            electricalProjectDistributionBoardDeviceId: z.string().min(1),
            count: z.number().min(1).max(200),
        }),
    },
    createAppCommandLogCommand: {
        createAppCommandLogCommandRecord: {
            recordIdField: z.string().nullable().optional(),
            recordId: z.string().nullable().optional(),
            bodySchema: z.object({
                recordIdField: z.string().nullable().optional(),
                recordId: z.string().nullable().optional(),
            }),
        },
        appKindEnum: ['ElectraApp', 'PortalApp'] as const,
        httpMethodEnum: [
            'Get',
            'Head',
            'Post',
            'Put',
            'Patch',
            'Delete',
            'Options',
        ] as const,
        appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
        appUrl: z.string().max(2048).nullable().optional(),
        appVersion: z.string().max(100).nullable().optional(),
        apiUrl: z.string().max(2048).nullable().optional(),
        apiHttpMethod: z
            .enum(['Get', 'Head', 'Post', 'Put', 'Patch', 'Delete', 'Options'])
            .nullable()
            .optional(),
        apiRequestId: z.string().nullable().optional(),
        date: z.date().nullable().optional(),
        commandName: z.string().max(128).nullable().optional(),
        path: z.string().max(2048).nullable().optional(),
        queryString: z.string().max(2048).nullable().optional(),
        body: z.string().max(1000000).nullable().optional(),
        fileName: z.string().max(256).nullable().optional(),
        fileContentType: z.string().max(128).nullable().optional(),
        fileSize: z.number().nullable().optional(),
        deviceId: z.string().max(200).nullable().optional(),
        deviceName: z.string().max(200).nullable().optional(),
        deviceNetwork: z.string().max(200).nullable().optional(),
        message: z.string().max(16000).nullable().optional(),
        exception: z.string().max(16000).nullable().optional(),
        bodySchema: z.object({
            appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
            appUrl: z.string().max(2048).nullable().optional(),
            appVersion: z.string().max(100).nullable().optional(),
            apiUrl: z.string().max(2048).nullable().optional(),
            apiHttpMethod: z
                .enum([
                    'Get',
                    'Head',
                    'Post',
                    'Put',
                    'Patch',
                    'Delete',
                    'Options',
                ])
                .nullable()
                .optional(),
            apiRequestId: z.string().nullable().optional(),
            date: z.date().nullable().optional(),
            commandName: z.string().max(128).nullable().optional(),
            path: z.string().max(2048).nullable().optional(),
            queryString: z.string().max(2048).nullable().optional(),
            body: z.string().max(1000000).nullable().optional(),
            fileName: z.string().max(256).nullable().optional(),
            fileContentType: z.string().max(128).nullable().optional(),
            fileSize: z.number().nullable().optional(),
            deviceId: z.string().max(200).nullable().optional(),
            deviceName: z.string().max(200).nullable().optional(),
            deviceNetwork: z.string().max(200).nullable().optional(),
            message: z.string().max(16000).nullable().optional(),
            exception: z.string().max(16000).nullable().optional(),
        }),
    },
    createAppLogCommand: {
        appKindEnum: ['ElectraApp', 'PortalApp'] as const,
        httpMethodEnum: [
            'Get',
            'Head',
            'Post',
            'Put',
            'Patch',
            'Delete',
            'Options',
        ] as const,
        appLogLevelEnum: ['Fatal', 'Error', 'Warn', 'Info', 'Debug'] as const,
        appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
        appUrl: z.string().max(2048).nullable().optional(),
        appVersion: z.string().max(100).nullable().optional(),
        apiUrl: z.string().max(2048).nullable().optional(),
        apiHttpMethod: z
            .enum(['Get', 'Head', 'Post', 'Put', 'Patch', 'Delete', 'Options'])
            .nullable()
            .optional(),
        apiRequestId: z.string().nullable().optional(),
        logLevel: z
            .enum(['Fatal', 'Error', 'Warn', 'Info', 'Debug'])
            .nullable()
            .optional(),
        date: z.date().nullable().optional(),
        deviceId: z.string().max(200).nullable().optional(),
        deviceName: z.string().max(200).nullable().optional(),
        deviceNetwork: z.string().max(200).nullable().optional(),
        message: z.string().max(16000).nullable().optional(),
        exception: z.string().max(16000).nullable().optional(),
        bodySchema: z.object({
            appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
            appUrl: z.string().max(2048).nullable().optional(),
            appVersion: z.string().max(100).nullable().optional(),
            apiUrl: z.string().max(2048).nullable().optional(),
            apiHttpMethod: z
                .enum([
                    'Get',
                    'Head',
                    'Post',
                    'Put',
                    'Patch',
                    'Delete',
                    'Options',
                ])
                .nullable()
                .optional(),
            apiRequestId: z.string().nullable().optional(),
            logLevel: z
                .enum(['Fatal', 'Error', 'Warn', 'Info', 'Debug'])
                .nullable()
                .optional(),
            date: z.date().nullable().optional(),
            deviceId: z.string().max(200).nullable().optional(),
            deviceName: z.string().max(200).nullable().optional(),
            deviceNetwork: z.string().max(200).nullable().optional(),
            message: z.string().max(16000).nullable().optional(),
            exception: z.string().max(16000).nullable().optional(),
        }),
    },
    createContactPersonCommand: {
        organizationId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            mobilePhoneNumber: z.string().max(60).nullable().optional(),
        }),
    },
    createDefaultElectricalCheckListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    createDefaultElectricalInspectionNormListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    createDefaultElectricalObservationStandardTextItemsCommand: {
        organizationId: z.string().min(1),
        bodySchema: z.object({
            organizationId: z.string().min(1),
        }),
    },
    createDocumentTemplateCommand: {
        documentPdfKindEnum: [
            'ElectricalProjectPriceQuote',
            'ElectricalProjectInspectionPlan',
            'ElectricalProjectScope8Report',
            'ElectricalProjectScope10Report',
            'ElectricalProjectScope12Report',
            'ElectricalProjectNen1010Report',
            'ElectricalProjectNen3140Report',
            'ElectricalProjectPowerShutdownPlan',
            'ElectricalProjectSeriousDefectReport',
            'ElectricalProjectObservationReport',
        ] as const,
        documentTemplateIntervalKindEnum: ['None', 'First', 'Repeat'] as const,
        organizationId: z.string().min(1),
        pdfKind: z.enum([
            'ElectricalProjectPriceQuote',
            'ElectricalProjectInspectionPlan',
            'ElectricalProjectScope8Report',
            'ElectricalProjectScope10Report',
            'ElectricalProjectScope12Report',
            'ElectricalProjectNen1010Report',
            'ElectricalProjectNen3140Report',
            'ElectricalProjectPowerShutdownPlan',
            'ElectricalProjectSeriousDefectReport',
            'ElectricalProjectObservationReport',
        ]),
        intervalKind: z.enum(['None', 'First', 'Repeat']),
        name: z.string().min(1).max(100),
        description: z.string().min(1).max(500),
        groupName: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            pdfKind: z.enum([
                'ElectricalProjectPriceQuote',
                'ElectricalProjectInspectionPlan',
                'ElectricalProjectScope8Report',
                'ElectricalProjectScope10Report',
                'ElectricalProjectScope12Report',
                'ElectricalProjectNen1010Report',
                'ElectricalProjectNen3140Report',
                'ElectricalProjectPowerShutdownPlan',
                'ElectricalProjectSeriousDefectReport',
                'ElectricalProjectObservationReport',
            ]),
            intervalKind: z.enum(['None', 'First', 'Repeat']),
            name: z.string().min(1).max(100),
            description: z.string().min(1).max(500),
            groupName: z.string().max(100).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalCheckListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    createElectricalCheckListItemCommand: {
        electricalCheckListItemKindEnum: [
            'DistributionBoard',
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Other',
            'Scope12InspectionPlanDocument',
            'Scope12VisualInspection',
            'Scope12AdditionalInspectionPoints',
            'Scope12DocumentationDCSide',
            'Scope12ManualAndDataSpecifications',
            'Scope12GeneralRemarks',
            'Scope12PVInstallationPowerLine',
            'Scope12InspectionExecution',
            'Scope12DocumentationACSide',
            'Scope12MeasurementsAndTesting',
            'Scope12VisualInspectionOtherParts',
            'Scope12RoofConstructionCalculation',
            'InspectionPlanSafetyGuidelinesObjectLocation',
            'InspectionPlanInstallationSchematicsAndDocumentation',
            'InspectionPlanTestsAndMeasurements',
        ] as const,
        electricalCheckListId: z.string().min(1),
        kind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Other',
            'Scope12InspectionPlanDocument',
            'Scope12VisualInspection',
            'Scope12AdditionalInspectionPoints',
            'Scope12DocumentationDCSide',
            'Scope12ManualAndDataSpecifications',
            'Scope12GeneralRemarks',
            'Scope12PVInstallationPowerLine',
            'Scope12InspectionExecution',
            'Scope12DocumentationACSide',
            'Scope12MeasurementsAndTesting',
            'Scope12VisualInspectionOtherParts',
            'Scope12RoofConstructionCalculation',
            'InspectionPlanSafetyGuidelinesObjectLocation',
            'InspectionPlanInstallationSchematicsAndDocumentation',
            'InspectionPlanTestsAndMeasurements',
        ]),
        enableNotApplicable: z.boolean().nullable().optional(),
        enableObservations: z.boolean().nullable().optional(),
        description: z.string().min(1).max(1000),
        positiveLabelText: z.string().max(20).nullable().optional(),
        negativeLabelText: z.string().max(20).nullable().optional(),
        notApplicableLabelText: z.string().max(20).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalCheckListId: z.string().min(1),
            kind: z.enum([
                'DistributionBoard',
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Other',
                'Scope12InspectionPlanDocument',
                'Scope12VisualInspection',
                'Scope12AdditionalInspectionPoints',
                'Scope12DocumentationDCSide',
                'Scope12ManualAndDataSpecifications',
                'Scope12GeneralRemarks',
                'Scope12PVInstallationPowerLine',
                'Scope12InspectionExecution',
                'Scope12DocumentationACSide',
                'Scope12MeasurementsAndTesting',
                'Scope12VisualInspectionOtherParts',
                'Scope12RoofConstructionCalculation',
                'InspectionPlanSafetyGuidelinesObjectLocation',
                'InspectionPlanInstallationSchematicsAndDocumentation',
                'InspectionPlanTestsAndMeasurements',
            ]),
            enableNotApplicable: z.boolean().nullable().optional(),
            enableObservations: z.boolean().nullable().optional(),
            description: z.string().min(1).max(1000),
            positiveLabelText: z.string().max(20).nullable().optional(),
            negativeLabelText: z.string().max(20).nullable().optional(),
            notApplicableLabelText: z.string().max(20).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalCheckListTextCommand: {
        createElectricalCheckListTextCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalObservationClassificationGroupKindEnum: [
                'A',
                'B',
                'C',
                'D',
                'E',
                'F',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                groupKind: z
                    .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalCheckListTextKindEnum: ['Remark', 'Recommendation'] as const,
        electricalCheckListItemId: z.string().min(1),
        kind: z.enum(['Remark', 'Recommendation']),
        description: z.string().min(1).max(1000),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalCheckListItemId: z.string().min(1),
            kind: z.enum(['Remark', 'Recommendation']),
            description: z.string().min(1).max(1000),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalInspectionNormItemCommand: {
        electricalInspectionNormKindEnum: [
            'NEN',
            'SCIOS',
            'NPR',
            'NTA',
            'Other',
        ] as const,
        electricalInspectionNormListId: z.string().min(1),
        normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
        name: z.string().max(1000).nullable().optional(),
        description: z.string().max(1000).nullable().optional(),
        order: z.number().nullable().optional(),
        isCustomInput: z.boolean(),
        bodySchema: z.object({
            electricalInspectionNormListId: z.string().min(1),
            normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
            name: z.string().max(1000).nullable().optional(),
            description: z.string().max(1000).nullable().optional(),
            order: z.number().nullable().optional(),
            isCustomInput: z.boolean(),
        }),
    },
    createElectricalInspectionNormListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    createElectricalMeasurementDeviceCalibrationCommand: {
        electricalMeasurementDeviceId: z.string().min(1),
        certificateNumber: z.string().min(1).max(500),
        startDate: z.date(),
        endDate: z.date(),
        bodySchema: z.object({
            electricalMeasurementDeviceId: z.string().min(1),
            certificateNumber: z.string().min(1).max(500),
            startDate: z.date(),
            endDate: z.date(),
        }),
    },
    createElectricalMeasurementDeviceCommand: {
        electricalMeasurementDeviceCalibrationKindEnum: [
            'Required',
            'Optional',
            'NotApplicable',
        ] as const,
        organizationId: z.string().min(1),
        userId: z.string().nullable().optional(),
        name: z.string().min(1).max(300),
        brandName: z.string().min(1).max(100),
        deviceType: z.string().min(1).max(100),
        serialNumber: z.string().min(1).max(100),
        calibrationKind: z.enum(['Required', 'Optional', 'NotApplicable']),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            userId: z.string().nullable().optional(),
            name: z.string().min(1).max(300),
            brandName: z.string().min(1).max(100),
            deviceType: z.string().min(1).max(100),
            serialNumber: z.string().min(1).max(100),
            calibrationKind: z.enum(['Required', 'Optional', 'NotApplicable']),
        }),
    },
    createElectricalObservationStandardTextItemCommand: {
        createElectricalObservationStandardTextItemCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalObservationClassificationGroupKindEnum: [
                'A',
                'B',
                'C',
                'D',
                'E',
                'F',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                groupKind: z
                    .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalObservationStandardObjectKindEnum: [
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
        ] as const,
        electricalObservationStandardTextItemKindEnum: [
            'Remark',
            'Recommendation',
        ] as const,
        organizationId: z.string().min(1),
        objectKind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
        ]),
        textKind: z.enum(['Remark', 'Recommendation']),
        description: z.string().min(1).max(1000),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            objectKind: z.enum([
                'DistributionBoard',
                'FinalCircuit',
                'DistributionBoardFile',
                'DistributionBoardDevice',
                'RisoMeasurement',
                'PVInverter',
                'PVString',
            ]),
            textKind: z.enum(['Remark', 'Recommendation']),
            description: z.string().min(1).max(1000),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectCheckListItemCommand: {
        electricalProjectCheckListItemObjectKindEnum: [
            'DistributionBoard',
            'FinalCircuit',
            'Project',
        ] as const,
        electricalProjectCheckListItemId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        electricalCheckListItemId: z.string().min(1),
        objectKind: z.enum(['DistributionBoard', 'FinalCircuit', 'Project']),
        objectId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectCheckListItemId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            electricalCheckListItemId: z.string().min(1),
            objectKind: z.enum([
                'DistributionBoard',
                'FinalCircuit',
                'Project',
            ]),
            objectId: z.string().min(1),
        }),
    },
    createElectricalProjectCommand: {
        createElectricalProjectCommandIsCombinationInspectionEnum: [
            'False',
            'True',
        ] as const,
        organizationId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
        isCombinationInspection: z
            .enum(['False', 'True'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            description: z.string().max(100).nullable().optional(),
            isCombinationInspection: z
                .enum(['False', 'True'])
                .nullable()
                .optional(),
        }),
    },
    createElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().nullable().optional(),
        electricalProjectOrganizationId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectContactPersonId: z.string().nullable().optional(),
            electricalProjectOrganizationId: z.string().min(1),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            mobilePhoneNumber: z.string().max(60).nullable().optional(),
            receivePriceQuote: z.boolean().nullable().optional(),
            receiveInvoice: z.boolean().nullable().optional(),
            receiveInspectionPlan: z.boolean().nullable().optional(),
            receiveInspectionReport: z.boolean().nullable().optional(),
            receivePowerShutdownPlan: z.boolean().nullable().optional(),
            isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        }),
    },
    createElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardKindEnum: [
            'MainDistributionBoard',
            'SubDistributionBoard',
            'ControlBox',
        ] as const,
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
        identifier: z.string().max(100).nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        skipCreateCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectVersionId: z.string().min(1),
            kind: z
                .enum([
                    'MainDistributionBoard',
                    'SubDistributionBoard',
                    'ControlBox',
                ])
                .nullable()
                .optional(),
            isPVInstallation: z.boolean().nullable().optional(),
            order: z.number().nullable().optional(),
            identifier: z.string().max(100).nullable().optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCurrentMeasurements: z.boolean().nullable().optional(),
            reportVoltageMeasurements: z.boolean().nullable().optional(),
            reportGroundImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportNeutralImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportResidualCurrentDevices: z.boolean().nullable().optional(),
            reportResidualCurrentCircuitBreakerDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportFinalCircuitProtectiveDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportIsolationResistanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
            skipCreateCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    createElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceKindEnum: [
            'ResidualCurrentDevice',
            'ResidualCurrentCircuitBreakerDevice',
            'FinalCircuitProtectiveDevice',
            'IsolationResistanceMeasurement',
        ] as const,
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalRisoValueSignEnum: ['<', '>', '='] as const,
        electricalProjectDistributionBoardDeviceId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectDistributionBoardId: z.string().min(1),
        deviceKind: z.enum([
            'ResidualCurrentDevice',
            'ResidualCurrentCircuitBreakerDevice',
            'FinalCircuitProtectiveDevice',
            'IsolationResistanceMeasurement',
        ]),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardDeviceId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectDistributionBoardId: z.string().min(1),
            deviceKind: z.enum([
                'ResidualCurrentDevice',
                'ResidualCurrentCircuitBreakerDevice',
                'FinalCircuitProtectiveDevice',
                'IsolationResistanceMeasurement',
            ]),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number().nullable().optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            residualCurrentDeviceType: z.string().max(50).nullable().optional(),
            ratedCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            tripDelay: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ratedResidualCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            fuseKind: z.string().max(50).nullable().optional(),
            trippingCharacteristic: z.string().max(50).nullable().optional(),
            breakerRatedCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isTestButtonFunctional: z.boolean().nullable().optional(),
            resistanceL1PE: z.number().nullable().optional(),
            resistanceL2PE: z.number().nullable().optional(),
            resistanceL3PE: z.number().nullable().optional(),
            resistanceNeutralPE: z.number().nullable().optional(),
            resistanceL1Neutral: z.number().nullable().optional(),
            resistanceL2Neutral: z.number().nullable().optional(),
            resistanceL3Neutral: z.number().nullable().optional(),
            measureResistanceBetweenPowerPhases: z
                .boolean()
                .nullable()
                .optional(),
            resistanceL1L2: z.number().nullable().optional(),
            resistanceL1L3: z.number().nullable().optional(),
            resistanceL2L3: z.number().nullable().optional(),
            risoSign: z.enum(['<', '>', '=']).nullable().optional(),
            risoValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportRisoMeasurements: z.boolean().nullable().optional(),
        }),
    },
    createElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileKindEnum: [
            'EnclosureOpenImage',
            'EnclosureClosedImage',
            'ExtraImage',
            'ThermalImage',
            'Measurements',
        ] as const,
        electricalProjectDistributionBoardFileId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectDistributionBoardId: z.string().min(1),
        kind: z.enum([
            'EnclosureOpenImage',
            'EnclosureClosedImage',
            'ExtraImage',
            'ThermalImage',
            'Measurements',
        ]),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number().nullable().optional(),
        remark: z.string().max(100).nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardFileId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectDistributionBoardId: z.string().min(1),
            kind: z.enum([
                'EnclosureOpenImage',
                'EnclosureClosedImage',
                'ExtraImage',
                'ThermalImage',
                'Measurements',
            ]),
            electricalProjectFileId: z.string().nullable().optional(),
            order: z.number().nullable().optional(),
            remark: z.string().max(100).nullable().optional(),
        }),
    },
    createElectricalProjectDocumentCommand: {
        createElectricalProjectDocumentKindEnum: [
            'PriceQuote',
            'InspectionPlan',
            'PowerShutdownPlan',
            'Reports',
            'ObservationReport',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'PriceQuote',
                'InspectionPlan',
                'PowerShutdownPlan',
                'Reports',
                'ObservationReport',
            ])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            kind: z
                .enum([
                    'PriceQuote',
                    'InspectionPlan',
                    'PowerShutdownPlan',
                    'Reports',
                    'ObservationReport',
                ])
                .nullable()
                .optional(),
        }),
    },
    createElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectVersionId: z.string().min(1),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectEquipotentialBondingMeasurementId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectVersionId: z.string().min(1),
            location: z.string().max(100).nullable().optional(),
            resistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectFileCommand: {
        electricalProjectFileKindEnum: [
            'ObjectImage',
            'DistributionBoard',
            'FinalCircuit',
            'Observation',
            'TechnicalDrawing',
            'Floorplan',
            'PVInstallation',
            'PVInverter',
            'PriceQuote',
            'InspectionPlan',
            'ObservationLog',
            'Measurements',
        ] as const,
        electricalProjectFileId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z.enum([
            'ObjectImage',
            'DistributionBoard',
            'FinalCircuit',
            'Observation',
            'TechnicalDrawing',
            'Floorplan',
            'PVInstallation',
            'PVInverter',
            'PriceQuote',
            'InspectionPlan',
            'ObservationLog',
            'Measurements',
        ]),
        order: z.number().nullable().optional(),
        description: z.string().max(200).nullable().optional(),
        bodySchema: z.object({
            electricalProjectFileId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            kind: z.enum([
                'ObjectImage',
                'DistributionBoard',
                'FinalCircuit',
                'Observation',
                'TechnicalDrawing',
                'Floorplan',
                'PVInstallation',
                'PVInverter',
                'PriceQuote',
                'InspectionPlan',
                'ObservationLog',
                'Measurements',
            ]),
            order: z.number().nullable().optional(),
            description: z.string().max(200).nullable().optional(),
        }),
    },
    createElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitKindEnum: [
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Space',
            'Building',
            'Other',
        ] as const,
        electricalProjectFinalCircuitId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        skipCreateCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectFinalCircuitId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            kind: z
                .enum([
                    'FinalCircuit',
                    'PhotovoltaicInverter',
                    'Machine',
                    'Space',
                    'Building',
                    'Other',
                ])
                .nullable()
                .optional(),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number().nullable().optional(),
            floorNumber: z.string().max(50).nullable().optional(),
            spaceNumber: z.string().max(50).nullable().optional(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
            skipCreateCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    createElectricalProjectFinalCircuitFileCommand: {
        electricalProjectFinalCircuitFileKindEnum: [
            'DetailsImage',
            'OverviewImage',
            'ExtraImage',
            'ThermalImage',
        ] as const,
        electricalProjectFinalCircuitFileId: z.string().nullable().optional(),
        electricalProjectFinalCircuitId: z.string().min(1),
        kind: z.enum([
            'DetailsImage',
            'OverviewImage',
            'ExtraImage',
            'ThermalImage',
        ]),
        electricalProjectFileId: z.string().min(1),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectFinalCircuitFileId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectFinalCircuitId: z.string().min(1),
            kind: z.enum([
                'DetailsImage',
                'OverviewImage',
                'ExtraImage',
                'ThermalImage',
            ]),
            electricalProjectFileId: z.string().min(1),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectInspectionDateCommand: {
        electricalProjectInspectionDateId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        date: z.date(),
        bodySchema: z.object({
            electricalProjectInspectionDateId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            date: z.date(),
        }),
    },
    createElectricalProjectInspectionPlanRemarkCommand: {
        electricalProjectInspectionPlanRemarkId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectVersionId: z.string().min(1),
        order: z.number().nullable().optional(),
        subject: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectInspectionPlanRemarkId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectVersionId: z.string().min(1),
            order: z.number().nullable().optional(),
            subject: z.string().max(100).nullable().optional(),
            remark: z.string().max(1000).nullable().optional(),
        }),
    },
    createElectricalProjectLinksCommand: {
        electricalProjectVersionId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
        }),
    },
    createElectricalProjectLvbagObjectCommand: {
        bAGObjectKindEnum: ['Address', 'Building'] as const,
        electricalProjectVersionId: z.string().min(1),
        objectKind: z.enum(['Address', 'Building']),
        bAGBuildingIdentifier: z
            .string()
            .min(1)
            .regex(/^[0-9]{4}10[0-9]{10}$/),
        bAGAddressIdentifier: z
            .string()
            .regex(/^[0-9]{4}20[0-9]{10}$/)
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            objectKind: z.enum(['Address', 'Building']),
            bAGBuildingIdentifier: z
                .string()
                .min(1)
                .regex(/^[0-9]{4}10[0-9]{10}$/),
            bAGAddressIdentifier: z
                .string()
                .regex(/^[0-9]{4}20[0-9]{10}$/)
                .nullable()
                .optional(),
        }),
    },
    createElectricalProjectMeasurementDeviceCommand: {
        electricalProjectVersionId: z.string().min(1),
        electricalMeasurementDeviceId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            electricalMeasurementDeviceId: z.string().min(1),
        }),
    },
    createElectricalProjectObservationCommand: {
        createElectricalProjectObservationCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalProjectObservationReportTextKindEnum: [
                'None',
                'Same',
                'Custom',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean(),
            remarkKind: z.enum(['None', 'Same', 'Custom']),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                enabled: z.boolean(),
                remarkKind: z.enum(['None', 'Same', 'Custom']),
                remark: z.string().max(1000).nullable().optional(),
                recommendation: z.string().max(1000).nullable().optional(),
                electricalObservationClassificationGroupId: z
                    .string()
                    .nullable()
                    .optional(),
                electricalObservationClassificationCodeId: z
                    .string()
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalProjectObservationObjectKindEnum: [
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'CheckListItem',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
            'DistributionBoardInformation',
            'DistributionBoardMeasurements',
            'DistributionBoardImages',
            'DistributionBoardThermalImages',
            'DistributionBoardGeneralInformation',
            'DistributionBoardCurrent',
            'DistributionBoardVoltage',
            'DistributionBoardCircuitImpedance',
            'DistributionBoardNetImpedance',
            'FinalCircuitImages',
            'FinalCircuitThermalImages',
        ] as const,
        electricalObservationClassificationCodeKindEnum: [
            'Severe',
            'Serious',
            'Minor',
            'Remark',
        ] as const,
        electricalProjectObservationId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        objectKind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'CheckListItem',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
            'DistributionBoardInformation',
            'DistributionBoardMeasurements',
            'DistributionBoardImages',
            'DistributionBoardThermalImages',
            'DistributionBoardGeneralInformation',
            'DistributionBoardCurrent',
            'DistributionBoardVoltage',
            'DistributionBoardCircuitImpedance',
            'DistributionBoardNetImpedance',
            'FinalCircuitImages',
            'FinalCircuitThermalImages',
        ]),
        objectId: z.string().min(1),
        order: z.number().nullable().optional(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            objectKind: z.enum([
                'DistributionBoard',
                'FinalCircuit',
                'DistributionBoardFile',
                'DistributionBoardDevice',
                'CheckListItem',
                'RisoMeasurement',
                'PVInverter',
                'PVString',
                'DistributionBoardInformation',
                'DistributionBoardMeasurements',
                'DistributionBoardImages',
                'DistributionBoardThermalImages',
                'DistributionBoardGeneralInformation',
                'DistributionBoardCurrent',
                'DistributionBoardVoltage',
                'DistributionBoardCircuitImpedance',
                'DistributionBoardNetImpedance',
                'FinalCircuitImages',
                'FinalCircuitThermalImages',
            ]),
            objectId: z.string().min(1),
            order: z.number().nullable().optional(),
            floorplanElectricalProjectFileId: z.string().nullable().optional(),
            floorplanPinCoordinateX: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            floorplanPinCoordinateY: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            locationRemark: z.string().max(100).nullable().optional(),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        }),
    },
    createElectricalProjectObservationFileCommand: {
        electricalProjectObservationId: z.string().min(1),
        electricalProjectFileId: z.string().min(1),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            electricalProjectFileId: z.string().min(1),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectObservationLogCommand: {
        createElectricalProjectObservationLogPortalCommandFile: {
            electricalProjectFileId: z.string().min(1),
            bodySchema: z.object({
                electricalProjectFileId: z.string().min(1),
            }),
        },
        createElectricalProjectObservationLogCommandStatusEnum: [
            'SolutionRejected',
            'SolutionApproved',
        ] as const,
        electricalProjectObservationId: z.string().min(1),
        status: z
            .enum(['SolutionRejected', 'SolutionApproved'])
            .nullable()
            .optional(),
        message: z.string().max(5000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            status: z
                .enum(['SolutionRejected', 'SolutionApproved'])
                .nullable()
                .optional(),
            message: z.string().max(5000).nullable().optional(),
        }),
    },
    createElectricalProjectObservationLogPortalCommand: {
        createElectricalProjectObservationLogPortalCommandUser: {
            name: z.string().min(1).max(100),
            email: z.string().min(1).max(256),
            organizationName: z.string().min(1).max(100),
            bodySchema: z.object({
                name: z.string().min(1).max(100),
                email: z.string().min(1).max(256),
                organizationName: z.string().min(1).max(100),
            }),
        },
        createElectricalProjectObservationLogPortalCommandFile: {
            electricalProjectFileId: z.string().min(1),
            bodySchema: z.object({
                electricalProjectFileId: z.string().min(1),
            }),
        },
        createElectricalProjectObservationLogPortalCommandStatusEnum: [
            'IssueReported',
            'WontSolveIssue',
            'ToSolveIssue',
            'IssueSolved',
        ] as const,
        electricalProjectObservationId: z.string().min(1),
        status: z
            .enum([
                'IssueReported',
                'WontSolveIssue',
                'ToSolveIssue',
                'IssueSolved',
            ])
            .nullable()
            .optional(),
        message: z.string().max(5000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            status: z
                .enum([
                    'IssueReported',
                    'WontSolveIssue',
                    'ToSolveIssue',
                    'IssueSolved',
                ])
                .nullable()
                .optional(),
            message: z.string().max(5000).nullable().optional(),
        }),
    },
    createElectricalProjectOrganizationCommand: {
        createElectricalProjectOrganizationCommandRegistration: {
            createElectricalProjectOrganizationCommandNLKvk: {
                kVKNumber: z
                    .string()
                    .min(1)
                    .regex(/^[0-9]{8}$/),
                branchNumber: z
                    .string()
                    .regex(/^[0-9]{12}$/)
                    .nullable()
                    .optional(),
                bodySchema: z.object({
                    kVKNumber: z
                        .string()
                        .min(1)
                        .regex(/^[0-9]{8}$/),
                    branchNumber: z
                        .string()
                        .regex(/^[0-9]{12}$/)
                        .nullable()
                        .optional(),
                }),
            },
            businessRegistrationSourceKindEnum: ['NLKVK'] as const,
            sourceKind: z.enum(['NLKVK']).nullable().optional(),
            bodySchema: z.object({
                sourceKind: z.enum(['NLKVK']).nullable().optional(),
            }),
        },
        createElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
            bodySchema: z.object({
                street: z.string().max(64).nullable().optional(),
                streetNumber: z
                    .string()
                    .max(32)
                    .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                    .nullable()
                    .optional(),
                postalCode: z
                    .string()
                    .max(16)
                    .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                    .nullable()
                    .optional(),
                locality: z.string().max(64).nullable().optional(),
                administrativeArea: z.string().max(64).nullable().optional(),
                countryId: z.string().nullable().optional(),
            }),
        },
        legalPersonKindEnum: ['NaturalPerson', 'JuridicalPerson'] as const,
        electricalProjectOrganizationId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        legalPersonKind: z.enum(['NaturalPerson', 'JuridicalPerson']),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean(),
        isObjectOwner: z.boolean(),
        isObjectAdministrator: z.boolean(),
        isObjectTenant: z.boolean(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectOrganizationId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            name: z.string().min(1).max(100),
            description: z.string().max(250).nullable().optional(),
            legalPersonKind: z.enum(['NaturalPerson', 'JuridicalPerson']),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            website: z.string().max(250).nullable().optional(),
            isProjectCustomer: z.boolean(),
            isObjectOwner: z.boolean(),
            isObjectAdministrator: z.boolean(),
            isObjectTenant: z.boolean(),
            isPVInstallationConstructionCompany: z
                .boolean()
                .nullable()
                .optional(),
        }),
    },
    createElectricalProjectPVInverterCommand: {
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalProjectPVInverterId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number().nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVInverterId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            brandModel: z.string().max(100).nullable().optional(),
            serialNumber: z.string().max(100).nullable().optional(),
            firmwareVersion: z.string().max(100).nullable().optional(),
            ratedPower: z.number().min(0).max(100000).nullable().optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            order: z.number().nullable().optional(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            groundImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportImpedanceMeasurements: z.boolean().nullable().optional(),
            reportPVStrings: z.boolean().nullable().optional(),
        }),
    },
    createElectricalProjectPVInverterFileCommand: {
        electricalPVInverterFileKindEnum: [
            'PVInverterImage',
            'PVStringTable',
            'PVInverterThermalImage',
        ] as const,
        electricalProjectPVInverterFileId: z.string().nullable().optional(),
        electricalProjectPVInverterId: z.string().min(1),
        electricalProjectFileId: z.string().min(1),
        kind: z.enum([
            'PVInverterImage',
            'PVStringTable',
            'PVInverterThermalImage',
        ]),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVInverterFileId: z.string().nullable().optional(),
            electricalProjectPVInverterId: z.string().min(1),
            electricalProjectFileId: z.string().min(1),
            kind: z.enum([
                'PVInverterImage',
                'PVStringTable',
                'PVInverterThermalImage',
            ]),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().nullable().optional(),
        electricalProjectPVInverterId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVStringId: z.string().nullable().optional(),
            electricalProjectPVInverterId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            voltage: z.number().min(0).max(100000).nullable().optional(),
            resistance: z.number().min(0).max(100000).nullable().optional(),
            current: z.number().min(0).max(100000).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectRisoMeasurementCommand: {
        electricalRisoMeasurementCircuitKindEnum: [
            'L-PE / N-PE',
            'L1-L2',
            'L2-L3',
            'L1-L3',
            'L1-N',
            'L2-N',
            'L3-N',
            'L1-PE',
            'L2-PE',
            'L3-PE',
            'N-PE',
            'L-PE',
        ] as const,
        electricalProjectRisoMeasurementId: z.string().nullable().optional(),
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectRisoMeasurementId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectDistributionBoardDeviceId: z.string().min(1),
            circuitKind: z
                .enum([
                    'L-PE / N-PE',
                    'L1-L2',
                    'L2-L3',
                    'L1-L3',
                    'L1-N',
                    'L2-N',
                    'L3-N',
                    'L1-PE',
                    'L2-PE',
                    'L3-PE',
                    'N-PE',
                    'L-PE',
                ])
                .nullable()
                .optional(),
            measuredValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            minimumValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isFaulty: z.boolean().nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().min(1).max(200),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectTechnicalDrawingId: z
                .string()
                .nullable()
                .optional(),
            electricalProjectVersionId: z.string().min(1),
            isDrawingPresent: z.boolean().nullable().optional(),
            electricalProjectFileId: z.string().nullable().optional(),
            name: z.string().min(1).max(200),
            location: z.string().max(200).nullable().optional(),
            description: z.string().max(500).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    createElectricalProjectUserCommand: {
        electricalProjectUserKindEnum: [
            'Inspector',
            'CertifiedInspector',
            'PriceQuoteSigner',
        ] as const,
        electricalProjectUserId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        userId: z.string().min(1),
        kind: z.enum(['Inspector', 'CertifiedInspector', 'PriceQuoteSigner']),
        bodySchema: z.object({
            electricalProjectUserId: z.string().nullable().optional(),
            electricalProjectVersionId: z.string().min(1),
            userId: z.string().min(1),
            kind: z.enum([
                'Inspector',
                'CertifiedInspector',
                'PriceQuoteSigner',
            ]),
        }),
    },
    createObjectCommand: {
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        constructionYear: z.number().min(1000).max(2100).nullable().optional(),
        street: z.string().min(1).max(64),
        streetNumber: z
            .string()
            .min(1)
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
        postalCode: z
            .string()
            .min(1)
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
        locality: z.string().min(1).max(64),
        administrativeArea: z.string().max(64).nullable().optional(),
        countryId: z.string().min(1),
        remarks: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            name: z.string().min(1).max(100),
            description: z.string().max(250).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            totalFloorArea: z
                .number()
                .min(0)
                .max(1000000)
                .nullable()
                .optional(),
            constructionYear: z
                .number()
                .min(1000)
                .max(2100)
                .nullable()
                .optional(),
            street: z.string().min(1).max(64),
            streetNumber: z
                .string()
                .min(1)
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
            postalCode: z
                .string()
                .min(1)
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
            locality: z.string().min(1).max(64),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().min(1),
            remarks: z.string().max(1000).nullable().optional(),
        }),
    },
    createObjectRflowCompanyLinkCommand: {
        objectId: z.string().min(1),
        rflowCompanyId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            objectId: z.string().min(1),
            rflowCompanyId: z.string().min(1),
            enabled: z.boolean(),
            linkAutomatically: z.boolean().nullable().optional(),
            remarks: z.string().max(500).nullable().optional(),
        }),
    },
    createObjectRflowInstallationLinkCommand: {
        objectId: z.string().min(1),
        rflowInstallationId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            objectId: z.string().min(1),
            rflowInstallationId: z.string().min(1),
            enabled: z.boolean(),
            linkAutomatically: z.boolean().nullable().optional(),
            remarks: z.string().max(500).nullable().optional(),
        }),
    },
    createOrganizationCommand: {
        createOrganizationCommandNLKvK: {
            kVKNumber: z
                .string()
                .min(1)
                .regex(/^[0-9]{8}$/),
            branchNumber: z
                .string()
                .regex(/^[0-9]{12}$/)
                .nullable()
                .optional(),
            bodySchema: z.object({
                kVKNumber: z
                    .string()
                    .min(1)
                    .regex(/^[0-9]{8}$/),
                branchNumber: z
                    .string()
                    .regex(/^[0-9]{12}$/)
                    .nullable()
                    .optional(),
            }),
        },
        businessRegistrationSourceKindEnum: ['NLKVK'] as const,
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        sourceKind: z.enum(['NLKVK']),
        slogan: z.string().max(250).nullable().optional(),
        chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        iBAN: z.string().max(40).nullable().optional(),
        bIC: z.string().max(15).nullable().optional(),
        vatNumber: z.string().max(30).nullable().optional(),
        sciosRegistrationNumber: z.string().max(30).nullable().optional(),
        enableElectricalProjectPriceQuote: z.boolean().nullable().optional(),
        enableElectricalProjectPlanning: z.boolean().nullable().optional(),
        enableElectricalProjectRecommendations: z
            .boolean()
            .nullable()
            .optional(),
        bodySchema: z.object({
            name: z.string().min(1).max(100),
            description: z.string().max(250).nullable().optional(),
            sourceKind: z.enum(['NLKVK']),
            slogan: z.string().max(250).nullable().optional(),
            chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            website: z.string().max(250).nullable().optional(),
            iBAN: z.string().max(40).nullable().optional(),
            bIC: z.string().max(15).nullable().optional(),
            vatNumber: z.string().max(30).nullable().optional(),
            sciosRegistrationNumber: z.string().max(30).nullable().optional(),
            enableElectricalProjectPriceQuote: z
                .boolean()
                .nullable()
                .optional(),
            enableElectricalProjectPlanning: z.boolean().nullable().optional(),
            enableElectricalProjectRecommendations: z
                .boolean()
                .nullable()
                .optional(),
        }),
    },
    createOrganizationObjectRelationCommand: {
        organizationObjectRelationKindEnum: [
            'ObjectOwner',
            'ObjectTenant',
            'ObjectAdministrator',
            'ObjectAdministratorElectricalInstallation',
            'ObjectAdministratorRefrigerantInstallation',
            'ObjectAdministratorAtex',
            'ObjectElectricalInstallationInspector',
            'ObjectRefrigerantInstallationInstaller',
        ] as const,
        organizationId: z.string().min(1),
        objectId: z.string().min(1),
        relationKind: z.enum([
            'ObjectOwner',
            'ObjectTenant',
            'ObjectAdministrator',
            'ObjectAdministratorElectricalInstallation',
            'ObjectAdministratorRefrigerantInstallation',
            'ObjectAdministratorAtex',
            'ObjectElectricalInstallationInspector',
            'ObjectRefrigerantInstallationInstaller',
        ]),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            objectId: z.string().min(1),
            relationKind: z.enum([
                'ObjectOwner',
                'ObjectTenant',
                'ObjectAdministrator',
                'ObjectAdministratorElectricalInstallation',
                'ObjectAdministratorRefrigerantInstallation',
                'ObjectAdministratorAtex',
                'ObjectElectricalInstallationInspector',
                'ObjectRefrigerantInstallationInstaller',
            ]),
        }),
    },
    createOrganizationUserCommand: {
        createUserCommandSettings: {
            displayName: z.string().min(1).max(50),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
            bodySchema: z.object({
                displayName: z.string().min(1).max(50),
                signatureText: z.string().max(50).nullable().optional(),
                firstName: z.string().max(50).nullable().optional(),
                middleName: z.string().max(20).nullable().optional(),
                lastName: z.string().max(50).nullable().optional(),
                department: z.string().max(100).nullable().optional(),
                jobTitleDescription: z.string().max(100).nullable().optional(),
                phoneNumber: z.string().max(60).nullable().optional(),
                colorCode: z.string().max(30).nullable().optional(),
            }),
        },
        organizationId: z.string().min(1),
        email: z.union([z.literal(''), z.string().email()]),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            email: z.union([z.literal(''), z.string().email()]),
        }),
    },
    createRepeatElectricalProjectCommand: {
        electricalProjectId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
        bodySchema: z.object({
            electricalProjectId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            description: z.string().max(100).nullable().optional(),
        }),
    },
    createUserCommand: {
        createUserCommandSettings: {
            displayName: z.string().min(1).max(50),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
            bodySchema: z.object({
                displayName: z.string().min(1).max(50),
                signatureText: z.string().max(50).nullable().optional(),
                firstName: z.string().max(50).nullable().optional(),
                middleName: z.string().max(20).nullable().optional(),
                lastName: z.string().max(50).nullable().optional(),
                department: z.string().max(100).nullable().optional(),
                jobTitleDescription: z.string().max(100).nullable().optional(),
                phoneNumber: z.string().max(60).nullable().optional(),
                colorCode: z.string().max(30).nullable().optional(),
            }),
        },
        email: z.union([z.literal(''), z.string().email()]),
        bodySchema: z.object({
            email: z.union([z.literal(''), z.string().email()]),
        }),
    },
    deleteDocumentTemplateSettingFileCommand: {
        documentTemplateSettingId: z.string().min(1),
        bodySchema: z.object({
            documentTemplateSettingId: z.string().min(1),
        }),
    },
    editAccountSettingsCommand: {
        displayName: z.string().max(50).nullable().optional(),
        signatureText: z.string().max(50).nullable().optional(),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        phoneNumber: z.string().max(60).nullable().optional(),
        colorCode: z.string().max(30).nullable().optional(),
        bodySchema: z.object({
            displayName: z.string().max(50).nullable().optional(),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
        }),
    },
    editContactPersonCommand: {
        contactPersonId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        bodySchema: z.object({
            contactPersonId: z.string().min(1),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            mobilePhoneNumber: z.string().max(60).nullable().optional(),
        }),
    },
    editDocumentTemplateCommand: {
        editDocumentTemplateCommandPdf: {
            ironPdfV1PageSizeEnum: [
                'Letter',
                'Legal',
                'A4',
                'CSheet',
                'DSheet',
                'ESheet',
                'LetterSmall',
                'Tabloid',
                'Ledger',
                'Statement',
                'Executive',
                'A3',
                'A4Small',
                'A5',
                'B4',
                'B5',
                'Folio',
                'Quarto',
                'Standard10x14',
                'Standard11x17',
                'Note',
                'Number9Envelope',
                'Number10Envelope',
                'Number11Envelope',
                'Number12Envelope',
                'Number14Envelope',
                'DLEnvelope',
                'C5Envelope',
                'C3Envelope',
                'C4Envelope',
                'C6Envelope',
                'C65Envelope',
                'B4Envelope',
                'B5Envelope',
                'B6Envelope',
                'ItalyEnvelope',
                'MonarchEnvelope',
                'PersonalEnvelope',
                'USStandardFanfold',
                'GermanStandardFanfold',
                'GermanLegalFanfold',
                'IsoB4',
                'JapanesePostcard',
                'Standard9x11',
                'Standard10x11',
                'Standard15x11',
                'InviteEnvelope',
                'LetterExtra',
                'LegalExtra',
                'TabloidExtra',
                'A4Extra',
                'LetterTransverse',
                'A4Transverse',
                'LetterExtraTransverse',
                'APlus',
                'BPlus',
                'LetterPlus',
                'A4Plus',
                'A5Transverse',
                'B5Transverse',
                'A3Extra',
                'A5Extra',
                'B5Extra',
                'A2',
                'A3Transverse',
                'A3ExtraTransverse',
                'JapaneseDoublePostcard',
                'A6',
                'LetterRotated',
                'A3Rotated',
                'A4Rotated',
                'A5Rotated',
                'B4JisRotated',
                'B5JisRotated',
                'JapanesePostcardRotated',
                'JapaneseDoublePostcardRotated',
                'A6Rotated',
                'B6Jis',
                'B6JisRotated',
                'Standard12x11',
                'Prc16K',
                'Prc32K',
                'Prc32KBig',
                'PrcEnvelopeNumber1',
                'PrcEnvelopeNumber2',
                'PrcEnvelopeNumber3',
                'PrcEnvelopeNumber4',
                'PrcEnvelopeNumber5',
                'PrcEnvelopeNumber6',
                'PrcEnvelopeNumber7',
                'PrcEnvelopeNumber8',
                'PrcEnvelopeNumber9',
                'PrcEnvelopeNumber10',
                'Prc16KRotated',
                'Prc32KRotated',
                'Prc32KBigRotated',
                'PrcEnvelopeNumber1Rotated',
                'PrcEnvelopeNumber2Rotated',
                'PrcEnvelopeNumber3Rotated',
                'PrcEnvelopeNumber4Rotated',
                'PrcEnvelopeNumber5Rotated',
                'PrcEnvelopeNumber6Rotated',
                'PrcEnvelopeNumber7Rotated',
                'PrcEnvelopeNumber8Rotated',
                'PrcEnvelopeNumber9Rotated',
                'PrcEnvelopeNumber10Rotated',
                'Custom',
            ] as const,
            pdfV1OrientationEnum: ['Landscape', 'Portrait'] as const,
            ironPdfV1PdfCssMediaTypeEnum: ['Print', 'Screen'] as const,
            ironPdfV1TableOfContentsTypesEnum: [
                'None',
                'Basic',
                'WithPageNumbers',
            ] as const,
            ironPdfV1FitToPaperModesEnum: [
                'Zoom',
                'FixedPixelWidth',
                'Automatic',
                'AutomaticFit',
                'ContinuousFeed',
            ] as const,
            pageSize: z
                .enum([
                    'Letter',
                    'Legal',
                    'A4',
                    'CSheet',
                    'DSheet',
                    'ESheet',
                    'LetterSmall',
                    'Tabloid',
                    'Ledger',
                    'Statement',
                    'Executive',
                    'A3',
                    'A4Small',
                    'A5',
                    'B4',
                    'B5',
                    'Folio',
                    'Quarto',
                    'Standard10x14',
                    'Standard11x17',
                    'Note',
                    'Number9Envelope',
                    'Number10Envelope',
                    'Number11Envelope',
                    'Number12Envelope',
                    'Number14Envelope',
                    'DLEnvelope',
                    'C5Envelope',
                    'C3Envelope',
                    'C4Envelope',
                    'C6Envelope',
                    'C65Envelope',
                    'B4Envelope',
                    'B5Envelope',
                    'B6Envelope',
                    'ItalyEnvelope',
                    'MonarchEnvelope',
                    'PersonalEnvelope',
                    'USStandardFanfold',
                    'GermanStandardFanfold',
                    'GermanLegalFanfold',
                    'IsoB4',
                    'JapanesePostcard',
                    'Standard9x11',
                    'Standard10x11',
                    'Standard15x11',
                    'InviteEnvelope',
                    'LetterExtra',
                    'LegalExtra',
                    'TabloidExtra',
                    'A4Extra',
                    'LetterTransverse',
                    'A4Transverse',
                    'LetterExtraTransverse',
                    'APlus',
                    'BPlus',
                    'LetterPlus',
                    'A4Plus',
                    'A5Transverse',
                    'B5Transverse',
                    'A3Extra',
                    'A5Extra',
                    'B5Extra',
                    'A2',
                    'A3Transverse',
                    'A3ExtraTransverse',
                    'JapaneseDoublePostcard',
                    'A6',
                    'LetterRotated',
                    'A3Rotated',
                    'A4Rotated',
                    'A5Rotated',
                    'B4JisRotated',
                    'B5JisRotated',
                    'JapanesePostcardRotated',
                    'JapaneseDoublePostcardRotated',
                    'A6Rotated',
                    'B6Jis',
                    'B6JisRotated',
                    'Standard12x11',
                    'Prc16K',
                    'Prc32K',
                    'Prc32KBig',
                    'PrcEnvelopeNumber1',
                    'PrcEnvelopeNumber2',
                    'PrcEnvelopeNumber3',
                    'PrcEnvelopeNumber4',
                    'PrcEnvelopeNumber5',
                    'PrcEnvelopeNumber6',
                    'PrcEnvelopeNumber7',
                    'PrcEnvelopeNumber8',
                    'PrcEnvelopeNumber9',
                    'PrcEnvelopeNumber10',
                    'Prc16KRotated',
                    'Prc32KRotated',
                    'Prc32KBigRotated',
                    'PrcEnvelopeNumber1Rotated',
                    'PrcEnvelopeNumber2Rotated',
                    'PrcEnvelopeNumber3Rotated',
                    'PrcEnvelopeNumber4Rotated',
                    'PrcEnvelopeNumber5Rotated',
                    'PrcEnvelopeNumber6Rotated',
                    'PrcEnvelopeNumber7Rotated',
                    'PrcEnvelopeNumber8Rotated',
                    'PrcEnvelopeNumber9Rotated',
                    'PrcEnvelopeNumber10Rotated',
                    'Custom',
                ])
                .nullable()
                .optional(),
            orientation: z
                .enum(['Landscape', 'Portrait'])
                .nullable()
                .optional(),
            marginLeft: z.number().nullable().optional(),
            marginRight: z.number().nullable().optional(),
            marginTop: z.number().nullable().optional(),
            marginBottom: z.number().nullable().optional(),
            enableJavaScript: z.boolean().nullable().optional(),
            javascript: z.string().max(10000).nullable().optional(),
            firstPageNumber: z.number().nullable().optional(),
            printHtmlBackgrounds: z.boolean().nullable().optional(),
            title: z.string().max(500).nullable().optional(),
            zoom: z.number().nullable().optional(),
            enableMathematicalLaTex: z.boolean().nullable().optional(),
            grayScale: z.boolean().nullable().optional(),
            cssMediaType: z.enum(['Print', 'Screen']).nullable().optional(),
            customCssUrl: z.string().max(250).nullable().optional(),
            createPdfFormsFromHtml: z.boolean().nullable().optional(),
            tableOfContentsTypes: z
                .enum(['None', 'Basic', 'WithPageNumbers'])
                .nullable()
                .optional(),
            forcePaperSize: z.boolean().nullable().optional(),
            fitToPaperModes: z
                .enum([
                    'Zoom',
                    'FixedPixelWidth',
                    'Automatic',
                    'AutomaticFit',
                    'ContinuousFeed',
                ])
                .nullable()
                .optional(),
            headerLoadStylesAndCSSFromMainHtmlDocument: z
                .boolean()
                .nullable()
                .optional(),
            headerUseCssFromMainDocument: z.boolean().nullable().optional(),
            headerMaxHeight: z.number().nullable().optional(),
            footerLoadStylesAndCSSFromMainHtmlDocument: z
                .boolean()
                .nullable()
                .optional(),
            footerUseCssFromMainDocument: z.boolean().nullable().optional(),
            footerMaxHeight: z.number().nullable().optional(),
            bodySchema: z.object({
                pageSize: z
                    .enum([
                        'Letter',
                        'Legal',
                        'A4',
                        'CSheet',
                        'DSheet',
                        'ESheet',
                        'LetterSmall',
                        'Tabloid',
                        'Ledger',
                        'Statement',
                        'Executive',
                        'A3',
                        'A4Small',
                        'A5',
                        'B4',
                        'B5',
                        'Folio',
                        'Quarto',
                        'Standard10x14',
                        'Standard11x17',
                        'Note',
                        'Number9Envelope',
                        'Number10Envelope',
                        'Number11Envelope',
                        'Number12Envelope',
                        'Number14Envelope',
                        'DLEnvelope',
                        'C5Envelope',
                        'C3Envelope',
                        'C4Envelope',
                        'C6Envelope',
                        'C65Envelope',
                        'B4Envelope',
                        'B5Envelope',
                        'B6Envelope',
                        'ItalyEnvelope',
                        'MonarchEnvelope',
                        'PersonalEnvelope',
                        'USStandardFanfold',
                        'GermanStandardFanfold',
                        'GermanLegalFanfold',
                        'IsoB4',
                        'JapanesePostcard',
                        'Standard9x11',
                        'Standard10x11',
                        'Standard15x11',
                        'InviteEnvelope',
                        'LetterExtra',
                        'LegalExtra',
                        'TabloidExtra',
                        'A4Extra',
                        'LetterTransverse',
                        'A4Transverse',
                        'LetterExtraTransverse',
                        'APlus',
                        'BPlus',
                        'LetterPlus',
                        'A4Plus',
                        'A5Transverse',
                        'B5Transverse',
                        'A3Extra',
                        'A5Extra',
                        'B5Extra',
                        'A2',
                        'A3Transverse',
                        'A3ExtraTransverse',
                        'JapaneseDoublePostcard',
                        'A6',
                        'LetterRotated',
                        'A3Rotated',
                        'A4Rotated',
                        'A5Rotated',
                        'B4JisRotated',
                        'B5JisRotated',
                        'JapanesePostcardRotated',
                        'JapaneseDoublePostcardRotated',
                        'A6Rotated',
                        'B6Jis',
                        'B6JisRotated',
                        'Standard12x11',
                        'Prc16K',
                        'Prc32K',
                        'Prc32KBig',
                        'PrcEnvelopeNumber1',
                        'PrcEnvelopeNumber2',
                        'PrcEnvelopeNumber3',
                        'PrcEnvelopeNumber4',
                        'PrcEnvelopeNumber5',
                        'PrcEnvelopeNumber6',
                        'PrcEnvelopeNumber7',
                        'PrcEnvelopeNumber8',
                        'PrcEnvelopeNumber9',
                        'PrcEnvelopeNumber10',
                        'Prc16KRotated',
                        'Prc32KRotated',
                        'Prc32KBigRotated',
                        'PrcEnvelopeNumber1Rotated',
                        'PrcEnvelopeNumber2Rotated',
                        'PrcEnvelopeNumber3Rotated',
                        'PrcEnvelopeNumber4Rotated',
                        'PrcEnvelopeNumber5Rotated',
                        'PrcEnvelopeNumber6Rotated',
                        'PrcEnvelopeNumber7Rotated',
                        'PrcEnvelopeNumber8Rotated',
                        'PrcEnvelopeNumber9Rotated',
                        'PrcEnvelopeNumber10Rotated',
                        'Custom',
                    ])
                    .nullable()
                    .optional(),
                orientation: z
                    .enum(['Landscape', 'Portrait'])
                    .nullable()
                    .optional(),
                marginLeft: z.number().nullable().optional(),
                marginRight: z.number().nullable().optional(),
                marginTop: z.number().nullable().optional(),
                marginBottom: z.number().nullable().optional(),
                enableJavaScript: z.boolean().nullable().optional(),
                javascript: z.string().max(10000).nullable().optional(),
                firstPageNumber: z.number().nullable().optional(),
                printHtmlBackgrounds: z.boolean().nullable().optional(),
                title: z.string().max(500).nullable().optional(),
                zoom: z.number().nullable().optional(),
                enableMathematicalLaTex: z.boolean().nullable().optional(),
                grayScale: z.boolean().nullable().optional(),
                cssMediaType: z.enum(['Print', 'Screen']).nullable().optional(),
                customCssUrl: z.string().max(250).nullable().optional(),
                createPdfFormsFromHtml: z.boolean().nullable().optional(),
                tableOfContentsTypes: z
                    .enum(['None', 'Basic', 'WithPageNumbers'])
                    .nullable()
                    .optional(),
                forcePaperSize: z.boolean().nullable().optional(),
                fitToPaperModes: z
                    .enum([
                        'Zoom',
                        'FixedPixelWidth',
                        'Automatic',
                        'AutomaticFit',
                        'ContinuousFeed',
                    ])
                    .nullable()
                    .optional(),
                headerLoadStylesAndCSSFromMainHtmlDocument: z
                    .boolean()
                    .nullable()
                    .optional(),
                headerUseCssFromMainDocument: z.boolean().nullable().optional(),
                headerMaxHeight: z.number().nullable().optional(),
                footerLoadStylesAndCSSFromMainHtmlDocument: z
                    .boolean()
                    .nullable()
                    .optional(),
                footerUseCssFromMainDocument: z.boolean().nullable().optional(),
                footerMaxHeight: z.number().nullable().optional(),
            }),
        },
        editDocumentTemplateCommandTailwind: {
            gradientPrimary: z.string().max(600).nullable().optional(),
            gradientSecondary: z.string().max(600).nullable().optional(),
            backgroundPrimary: z.string().max(50).nullable().optional(),
            backgroundSecondary: z.string().max(50).nullable().optional(),
            backgroundTertiary: z.string().max(50).nullable().optional(),
            backgroundQuaternary: z.string().max(50).nullable().optional(),
            backgroundPopover: z.string().max(50).nullable().optional(),
            backgroundTab: z.string().max(50).nullable().optional(),
            inputBorder: z.string().max(50).nullable().optional(),
            inputBorderShadow: z.string().max(50).nullable().optional(),
            textPrimary: z.string().max(50).nullable().optional(),
            textSecondary: z.string().max(50).nullable().optional(),
            textTertiary: z.string().max(50).nullable().optional(),
            textQuaternary: z.string().max(50).nullable().optional(),
            iconPrimary: z.string().max(50).nullable().optional(),
            iconSecondary: z.string().max(50).nullable().optional(),
            brandPrimary: z.string().max(50).nullable().optional(),
            brandSecondary: z.string().max(50).nullable().optional(),
            link: z.string().max(50).nullable().optional(),
            radius: z.string().max(50).nullable().optional(),
            fontSizeH1: z.string().max(50).nullable().optional(),
            fontSizeH2: z.string().max(50).nullable().optional(),
            fontSizeH3: z.string().max(50).nullable().optional(),
            fontSizeH4: z.string().max(50).nullable().optional(),
            fontSizeH5: z.string().max(50).nullable().optional(),
            fontSizeH6: z.string().max(50).nullable().optional(),
            fontSizeP: z.string().max(50).nullable().optional(),
            fontSizeLi: z.string().max(50).nullable().optional(),
            fontSizeTd: z.string().max(50).nullable().optional(),
            fontFamily: z.string().max(50).nullable().optional(),
            lineHeightH1: z.string().max(50).nullable().optional(),
            lineHeightH2: z.string().max(50).nullable().optional(),
            lineHeightH3: z.string().max(50).nullable().optional(),
            lineHeightH4: z.string().max(50).nullable().optional(),
            lineHeightH5: z.string().max(50).nullable().optional(),
            lineHeightH6: z.string().max(50).nullable().optional(),
            lineHeightP: z.string().max(50).nullable().optional(),
            lineHeightLi: z.string().max(50).nullable().optional(),
            lineHeightTd: z.string().max(50).nullable().optional(),
            fontWeightH1: z.string().max(50).nullable().optional(),
            fontWeightH2: z.string().max(50).nullable().optional(),
            fontWeightH3: z.string().max(50).nullable().optional(),
            fontWeightH4: z.string().max(50).nullable().optional(),
            fontWeightH5: z.string().max(50).nullable().optional(),
            fontWeightH6: z.string().max(50).nullable().optional(),
            fontWeightP: z.string().max(50).nullable().optional(),
            fontWeightLi: z.string().max(50).nullable().optional(),
            fontWeightTd: z.string().max(50).nullable().optional(),
            indentLength: z.string().max(50).nullable().optional(),
            bodySchema: z.object({
                gradientPrimary: z.string().max(600).nullable().optional(),
                gradientSecondary: z.string().max(600).nullable().optional(),
                backgroundPrimary: z.string().max(50).nullable().optional(),
                backgroundSecondary: z.string().max(50).nullable().optional(),
                backgroundTertiary: z.string().max(50).nullable().optional(),
                backgroundQuaternary: z.string().max(50).nullable().optional(),
                backgroundPopover: z.string().max(50).nullable().optional(),
                backgroundTab: z.string().max(50).nullable().optional(),
                inputBorder: z.string().max(50).nullable().optional(),
                inputBorderShadow: z.string().max(50).nullable().optional(),
                textPrimary: z.string().max(50).nullable().optional(),
                textSecondary: z.string().max(50).nullable().optional(),
                textTertiary: z.string().max(50).nullable().optional(),
                textQuaternary: z.string().max(50).nullable().optional(),
                iconPrimary: z.string().max(50).nullable().optional(),
                iconSecondary: z.string().max(50).nullable().optional(),
                brandPrimary: z.string().max(50).nullable().optional(),
                brandSecondary: z.string().max(50).nullable().optional(),
                link: z.string().max(50).nullable().optional(),
                radius: z.string().max(50).nullable().optional(),
                fontSizeH1: z.string().max(50).nullable().optional(),
                fontSizeH2: z.string().max(50).nullable().optional(),
                fontSizeH3: z.string().max(50).nullable().optional(),
                fontSizeH4: z.string().max(50).nullable().optional(),
                fontSizeH5: z.string().max(50).nullable().optional(),
                fontSizeH6: z.string().max(50).nullable().optional(),
                fontSizeP: z.string().max(50).nullable().optional(),
                fontSizeLi: z.string().max(50).nullable().optional(),
                fontSizeTd: z.string().max(50).nullable().optional(),
                fontFamily: z.string().max(50).nullable().optional(),
                lineHeightH1: z.string().max(50).nullable().optional(),
                lineHeightH2: z.string().max(50).nullable().optional(),
                lineHeightH3: z.string().max(50).nullable().optional(),
                lineHeightH4: z.string().max(50).nullable().optional(),
                lineHeightH5: z.string().max(50).nullable().optional(),
                lineHeightH6: z.string().max(50).nullable().optional(),
                lineHeightP: z.string().max(50).nullable().optional(),
                lineHeightLi: z.string().max(50).nullable().optional(),
                lineHeightTd: z.string().max(50).nullable().optional(),
                fontWeightH1: z.string().max(50).nullable().optional(),
                fontWeightH2: z.string().max(50).nullable().optional(),
                fontWeightH3: z.string().max(50).nullable().optional(),
                fontWeightH4: z.string().max(50).nullable().optional(),
                fontWeightH5: z.string().max(50).nullable().optional(),
                fontWeightH6: z.string().max(50).nullable().optional(),
                fontWeightP: z.string().max(50).nullable().optional(),
                fontWeightLi: z.string().max(50).nullable().optional(),
                fontWeightTd: z.string().max(50).nullable().optional(),
                indentLength: z.string().max(50).nullable().optional(),
            }),
        },
        editDocumentTemplateCommandSetting: {
            imageQualityEnum: [
                'Original',
                'OriginalWebp',
                'ExtraLarge',
                'Large',
                'Medium',
                'Small',
                'ExtraSmall',
            ] as const,
            documentTemplateSettingId: z.string().min(1),
            enabled: z.boolean(),
            imageQuality: z
                .enum([
                    'Original',
                    'OriginalWebp',
                    'ExtraLarge',
                    'Large',
                    'Medium',
                    'Small',
                    'ExtraSmall',
                ])
                .nullable()
                .optional(),
            toggle: z.boolean().nullable().optional(),
            wholeNumber: z.number().nullable().optional(),
            decimalNumber: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            date: z.date().nullable().optional(),
            dateTime: z.date().nullable().optional(),
            dateTimeOffset: z.date().nullable().optional(),
            text: z.string().max(200000).nullable().optional(),
            bodySchema: z.object({
                documentTemplateSettingId: z.string().min(1),
                enabled: z.boolean(),
                imageQuality: z
                    .enum([
                        'Original',
                        'OriginalWebp',
                        'ExtraLarge',
                        'Large',
                        'Medium',
                        'Small',
                        'ExtraSmall',
                    ])
                    .nullable()
                    .optional(),
                toggle: z.boolean().nullable().optional(),
                wholeNumber: z.number().nullable().optional(),
                decimalNumber: z
                    .number()
                    .min(-1000000000)
                    .max(1000000000)
                    .nullable()
                    .optional(),
                date: z.date().nullable().optional(),
                dateTime: z.date().nullable().optional(),
                dateTimeOffset: z.date().nullable().optional(),
                text: z.string().max(200000).nullable().optional(),
            }),
        },
        documentTemplateId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().min(1).max(500),
        groupName: z.string().max(100).nullable().optional(),
        order: z.number(),
        isDefault: z.boolean(),
        isPublic: z.boolean(),
        enabled: z.boolean(),
        bodySchema: z.object({
            documentTemplateId: z.string().min(1),
            name: z.string().min(1).max(100),
            description: z.string().min(1).max(500),
            groupName: z.string().max(100).nullable().optional(),
            order: z.number(),
            isDefault: z.boolean(),
            isPublic: z.boolean(),
            enabled: z.boolean(),
        }),
    },
    editElectricalCheckListCommand: {
        electricalCheckListId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            electricalCheckListId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    editElectricalCheckListItemCommand: {
        electricalCheckListItemId: z.string().min(1),
        enableNotApplicable: z.boolean().nullable().optional(),
        enableObservations: z.boolean().nullable().optional(),
        description: z.string().min(1).max(1000),
        positiveLabelText: z.string().max(20).nullable().optional(),
        negativeLabelText: z.string().max(20).nullable().optional(),
        notApplicableLabelText: z.string().max(20).nullable().optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalCheckListItemId: z.string().min(1),
            enableNotApplicable: z.boolean().nullable().optional(),
            enableObservations: z.boolean().nullable().optional(),
            description: z.string().min(1).max(1000),
            positiveLabelText: z.string().max(20).nullable().optional(),
            negativeLabelText: z.string().max(20).nullable().optional(),
            notApplicableLabelText: z.string().max(20).nullable().optional(),
            order: z.number(),
        }),
    },
    editElectricalCheckListTextCommand: {
        editElectricalCheckListTextCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalObservationClassificationGroupKindEnum: [
                'A',
                'B',
                'C',
                'D',
                'E',
                'F',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                groupKind: z
                    .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalCheckListTextId: z.string().min(1),
        text: z.string().min(1).max(1000),
        bodySchema: z.object({
            electricalCheckListTextId: z.string().min(1),
            text: z.string().min(1).max(1000),
        }),
    },
    editElectricalInspectionNormItemCommand: {
        electricalInspectionNormKindEnum: [
            'NEN',
            'SCIOS',
            'NPR',
            'NTA',
            'Other',
        ] as const,
        electricalInspectionNormItemId: z.string().min(1),
        normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
        name: z.string().max(1000).nullable().optional(),
        description: z.string().max(1000).nullable().optional(),
        order: z.number(),
        isCustomInput: z.boolean(),
        bodySchema: z.object({
            electricalInspectionNormItemId: z.string().min(1),
            normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
            name: z.string().max(1000).nullable().optional(),
            description: z.string().max(1000).nullable().optional(),
            order: z.number(),
            isCustomInput: z.boolean(),
        }),
    },
    editElectricalInspectionNormListCommand: {
        electricalInspectionNormListId: z.string().min(1),
        description: z.string().min(1).max(100),
        bodySchema: z.object({
            electricalInspectionNormListId: z.string().min(1),
            description: z.string().min(1).max(100),
        }),
    },
    editElectricalMeasurementDeviceCommand: {
        electricalMeasurementDeviceId: z.string().min(1),
        userId: z.string().nullable().optional(),
        name: z.string().min(1).max(300),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
        bodySchema: z.object({
            electricalMeasurementDeviceId: z.string().min(1),
            userId: z.string().nullable().optional(),
            name: z.string().min(1).max(300),
            startDate: z.date().nullable().optional(),
            endDate: z.date().nullable().optional(),
        }),
    },
    editElectricalObservationStandardTextItemCommand: {
        editElectricalObservationStandardTextItemCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalObservationClassificationGroupKindEnum: [
                'A',
                'B',
                'C',
                'D',
                'E',
                'F',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                groupKind: z
                    .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalObservationStandardTextItemId: z.string().min(1),
        description: z.string().min(1).max(1000),
        order: z.number(),
        bodySchema: z.object({
            electricalObservationStandardTextItemId: z.string().min(1),
            description: z.string().min(1).max(1000),
            order: z.number(),
        }),
    },
    editElectricalProjectCheckListItemCommand: {
        electricalProjectCheckListItemConclusionKindEnum: [
            'Approved',
            'NotApproved',
            'NotApplicable',
        ] as const,
        electricalProjectCheckListItemId: z.string().min(1),
        conclusionKind: z
            .enum(['Approved', 'NotApproved', 'NotApplicable'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectCheckListItemId: z.string().min(1),
            conclusionKind: z
                .enum(['Approved', 'NotApproved', 'NotApplicable'])
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectContactPersonId: z.string().min(1),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            mobilePhoneNumber: z.string().max(60).nullable().optional(),
            receivePriceQuote: z.boolean().nullable().optional(),
            receiveInvoice: z.boolean().nullable().optional(),
            receiveInspectionPlan: z.boolean().nullable().optional(),
            receiveInspectionReport: z.boolean().nullable().optional(),
            receivePowerShutdownPlan: z.boolean().nullable().optional(),
            isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardKindEnum: [
            'MainDistributionBoard',
            'SubDistributionBoard',
            'ControlBox',
        ] as const,
        electricalEarthingSystemKindEnum: [
            'TT',
            'TN-S',
            'TN-C',
            'TN-CS',
            'IT',
        ] as const,
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalApplicanceProtectionClassEnum: [
            '0',
            '0I',
            'I',
            'II',
            'IIFE',
            'III',
        ] as const,
        electricalDistributionBoardUpstreamProtectiveDeviceKindEnum: [
            'DiazedFuse',
            'FuseLink',
            'CircuitBreaker',
            'Unknown',
            'PowerCircuitBreaker',
        ] as const,
        electricalPowerPhaseRotationEnum: [
            'Clockwise',
            'CounterClockwise',
            'NotApplicable',
        ] as const,
        electricalProjectDistributionBoardId: z.string().min(1),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        parentElectricalProjectDistributionBoardId: z
            .string()
            .nullable()
            .optional(),
        order: z.number(),
        identifier: z.string().max(100).nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        earthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        electricalIngressProtectionRatingId: z.string().nullable().optional(),
        customElectricalIngressProtectionRating: z
            .string()
            .nullable()
            .optional(),
        applicanceProtectionClass: z
            .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
            .nullable()
            .optional(),
        mainSwitchNominalCurrent: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        upstreamProtectiveDeviceKind: z
            .enum([
                'DiazedFuse',
                'FuseLink',
                'CircuitBreaker',
                'Unknown',
                'PowerCircuitBreaker',
            ])
            .nullable()
            .optional(),
        upstreamProtectiveDeviceCharacteristic: z
            .string()
            .max(50)
            .nullable()
            .optional(),
        powerCircuitBreakerType: z.string().max(100).nullable().optional(),
        powerCircuitBreakerSetting: z.string().max(100).nullable().optional(),
        powerCircuitBreakerCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentNeutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageNeutralGround: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        powerPhaseRotation: z
            .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
            .nullable()
            .optional(),
        groundImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        measureImpedanceBetweenPowerPhases: z.boolean().nullable().optional(),
        impedanceCurrentL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardId: z.string().min(1),
            kind: z
                .enum([
                    'MainDistributionBoard',
                    'SubDistributionBoard',
                    'ControlBox',
                ])
                .nullable()
                .optional(),
            isPVInstallation: z.boolean().nullable().optional(),
            parentElectricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            order: z.number(),
            identifier: z.string().max(100).nullable().optional(),
            floorNumber: z.string().max(50).nullable().optional(),
            spaceNumber: z.string().max(50).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            earthingSystemKind: z
                .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
                .nullable()
                .optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            electricalIngressProtectionRatingId: z
                .string()
                .nullable()
                .optional(),
            customElectricalIngressProtectionRating: z
                .string()
                .nullable()
                .optional(),
            applicanceProtectionClass: z
                .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
                .nullable()
                .optional(),
            mainSwitchNominalCurrent: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            upstreamProtectiveDeviceKind: z
                .enum([
                    'DiazedFuse',
                    'FuseLink',
                    'CircuitBreaker',
                    'Unknown',
                    'PowerCircuitBreaker',
                ])
                .nullable()
                .optional(),
            upstreamProtectiveDeviceCharacteristic: z
                .string()
                .max(50)
                .nullable()
                .optional(),
            powerCircuitBreakerType: z.string().max(100).nullable().optional(),
            powerCircuitBreakerSetting: z
                .string()
                .max(100)
                .nullable()
                .optional(),
            powerCircuitBreakerCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentNeutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageNeutralGround: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            powerPhaseRotation: z
                .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
                .nullable()
                .optional(),
            groundImpedanceCurrentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceCurrentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceCurrentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            measureImpedanceBetweenPowerPhases: z
                .boolean()
                .nullable()
                .optional(),
            impedanceCurrentL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceCurrentL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceCurrentL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCurrentMeasurements: z.boolean().nullable().optional(),
            reportVoltageMeasurements: z.boolean().nullable().optional(),
            reportGroundImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportNeutralImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportResidualCurrentDevices: z.boolean().nullable().optional(),
            reportResidualCurrentCircuitBreakerDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportFinalCircuitProtectiveDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportIsolationResistanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectDistributionBoardDeviceCommand: {
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalRisoValueSignEnum: ['<', '>', '='] as const,
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z.number().nullable().optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z.number().nullable().optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z.number().nullable().optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardDeviceId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            residualCurrentDeviceType: z.string().max(50).nullable().optional(),
            ratedCurrent: z.number().nullable().optional(),
            tripDelay: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ratedResidualCurrent: z.number().nullable().optional(),
            fuseKind: z.string().max(50).nullable().optional(),
            trippingCharacteristic: z.string().max(50).nullable().optional(),
            breakerRatedCurrent: z.number().nullable().optional(),
            isTestButtonFunctional: z.boolean().nullable().optional(),
            resistanceL1PE: z.number().nullable().optional(),
            resistanceL2PE: z.number().nullable().optional(),
            resistanceL3PE: z.number().nullable().optional(),
            resistanceNeutralPE: z.number().nullable().optional(),
            resistanceL1Neutral: z.number().nullable().optional(),
            resistanceL2Neutral: z.number().nullable().optional(),
            resistanceL3Neutral: z.number().nullable().optional(),
            measureResistanceBetweenPowerPhases: z
                .boolean()
                .nullable()
                .optional(),
            resistanceL1L2: z.number().nullable().optional(),
            resistanceL1L3: z.number().nullable().optional(),
            resistanceL2L3: z.number().nullable().optional(),
            risoSign: z.enum(['<', '>', '=']).nullable().optional(),
            risoValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportRisoMeasurements: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileId: z.string().min(1),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number(),
        remark: z.string().max(100).nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardFileId: z.string().min(1),
            electricalProjectFileId: z.string().nullable().optional(),
            order: z.number(),
            remark: z.string().max(100).nullable().optional(),
        }),
    },
    editElectricalProjectDocumentTemplateCommand: {
        editElectricalProjectDocumentTemplateCommandTemplate: {
            electricalProjectDocumentTemplateId: z.string().min(1),
            selected: z.boolean(),
            bodySchema: z.object({
                electricalProjectDocumentTemplateId: z.string().min(1),
                selected: z.boolean(),
            }),
        },
        electricalProjectVersionId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
        }),
    },
    editElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z.string().min(1),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalProjectEquipotentialBondingMeasurementId: z
                .string()
                .min(1),
            location: z.string().max(100).nullable().optional(),
            resistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            order: z.number(),
        }),
    },
    editElectricalProjectFileCommand: {
        electricalProjectFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
        bodySchema: z.object({
            electricalProjectFileId: z.string().min(1),
            order: z.number(),
            description: z.string().max(200).nullable().optional(),
        }),
    },
    editElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitKindEnum: [
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Space',
            'Building',
            'Other',
        ] as const,
        electricalProjectFinalCircuitId: z.string().min(1),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectFinalCircuitId: z.string().min(1),
            kind: z
                .enum([
                    'FinalCircuit',
                    'PhotovoltaicInverter',
                    'Machine',
                    'Space',
                    'Building',
                    'Other',
                ])
                .nullable()
                .optional(),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number(),
            floorNumber: z.string().max(50).nullable().optional(),
            spaceNumber: z.string().max(50).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectInspectionCommand: {
        electricalProjectInspectionKindEnum: [
            'Inspection',
            'RepeatInspection',
        ] as const,
        electricalProjectDistributionBoardMeasurementRegistrationKindEnum: [
            'Input',
            'Attachment',
            'Request',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z
            .enum(['Inspection', 'RepeatInspection'])
            .nullable()
            .optional(),
        reportNumber: z.string().max(100).nullable().optional(),
        distributionBoardMeasurementRegistrationKind: z
            .enum(['Input', 'Attachment', 'Request'])
            .nullable()
            .optional(),
        enableExtendedRisoOnDistributionBoardDevices: z
            .boolean()
            .nullable()
            .optional(),
        pVSolarIrradiance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        pVSolarPanelTemperature: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isPVInstallationEquipotentialBondingPassed: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationEquipotentialBondingPassedRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isPVInstallationConstructionCompanyKnown: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationConstructionDocumentNumber: z
            .string()
            .max(100)
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z
                .enum(['Inspection', 'RepeatInspection'])
                .nullable()
                .optional(),
            reportNumber: z.string().max(100).nullable().optional(),
            distributionBoardMeasurementRegistrationKind: z
                .enum(['Input', 'Attachment', 'Request'])
                .nullable()
                .optional(),
            enableExtendedRisoOnDistributionBoardDevices: z
                .boolean()
                .nullable()
                .optional(),
            pVSolarIrradiance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            pVSolarPanelTemperature: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isPVInstallationEquipotentialBondingPassed: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationEquipotentialBondingPassedRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isPVInstallationConstructionCompanyKnown: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationConstructionDocumentNumber: z
                .string()
                .max(100)
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectInspectionConclusionCommand: {
        editElectricalProjectInspectionConclusionCommandScope: {
            sciosInspectionCode: z.string().max(20).nullable().optional(),
            bodySchema: z.object({
                sciosInspectionCode: z.string().max(20).nullable().optional(),
            }),
        },
        editElectricalProjectInspectionConclusionCommandIntervalQuestions: {
            electricalInspectionDetermineIntervalKindEnum: [
                'Age1',
                'Age2',
                'Age3',
                'Age4',
                'Quality1',
                'Quality2',
                'Quality3',
                'Quality4',
                'Quality5',
                'Environment1',
                'Environment2',
                'Environment3',
                'Users1',
                'Users2',
                'Users3',
                'Users4',
                'Supervision1',
                'Supervision2',
            ] as const,
            determineInspectionIntervalAge: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            bodySchema: z.object({
                determineInspectionIntervalAge: z
                    .enum([
                        'Age1',
                        'Age2',
                        'Age3',
                        'Age4',
                        'Quality1',
                        'Quality2',
                        'Quality3',
                        'Quality4',
                        'Quality5',
                        'Environment1',
                        'Environment2',
                        'Environment3',
                        'Users1',
                        'Users2',
                        'Users3',
                        'Users4',
                        'Supervision1',
                        'Supervision2',
                    ])
                    .nullable()
                    .optional(),
            }),
        },
        electricalInspectionIntervalDeterminedByKindEnum: [
            'InsuranceCompany',
            'Inspection',
            'RentalContract',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        scope10InspectionIntervalTableCellId: z.number().nullable().optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            nextInspectionInYearCount: z
                .number()
                .min(0)
                .max(15)
                .nullable()
                .optional(),
            scope10InspectionIntervalTableCellId: z
                .number()
                .nullable()
                .optional(),
            intervalDeterminedByKind: z
                .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
                .nullable()
                .optional(),
            includeRepairStatement: z.boolean().nullable().optional(),
            isPerformedAccordingToInspectionPlan: z
                .boolean()
                .nullable()
                .optional(),
            performedAccordingToInspectionPlanRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isFinalVerdictPassed: z.boolean().nullable().optional(),
            finalVerdictRemark: z.string().max(500).nullable().optional(),
        }),
    },
    editElectricalProjectInspectionConclusionReportCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalInspectionIntervalDeterminedByKindEnum: [
            'InsuranceCompany',
            'Inspection',
            'RentalContract',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        sciosInspectionCode: z.string().max(20).nullable().optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            nextInspectionInYearCount: z
                .number()
                .min(0)
                .max(15)
                .nullable()
                .optional(),
            intervalDeterminedByKind: z
                .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
                .nullable()
                .optional(),
            sciosInspectionCode: z.string().max(20).nullable().optional(),
            includeRepairStatement: z.boolean().nullable().optional(),
            isPerformedAccordingToInspectionPlan: z
                .boolean()
                .nullable()
                .optional(),
            performedAccordingToInspectionPlanRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isFinalVerdictPassed: z.boolean().nullable().optional(),
            finalVerdictRemark: z.string().max(500).nullable().optional(),
        }),
    },
    editElectricalProjectInspectionNormCommand: {
        electricalProjectInspectionNormStatusEnum: [
            'Selected',
            'NotSelected',
            'NotApplicable',
        ] as const,
        electricalProjectInspectionNormId: z.string().min(1),
        status: z.enum(['Selected', 'NotSelected', 'NotApplicable']),
        customDescription: z.string().max(1000).nullable().optional(),
        version: z.string().max(100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectInspectionNormId: z.string().min(1),
            status: z.enum(['Selected', 'NotSelected', 'NotApplicable']),
            customDescription: z.string().max(1000).nullable().optional(),
            version: z.string().max(100).nullable().optional(),
            remarks: z.string().max(1000).nullable().optional(),
        }),
    },
    editElectricalProjectInspectionPlanCommand: {
        electricalInspectionSampleMethodKindEnum: [
            'ConformNen3140AppendixJOrNTA8220Tabel1',
            'AdditionalInstruction',
            'Complete',
            'LimitToFirstSampleRound',
            'SampleCanBeExpandedBasedOnAppendixJ',
            'TestingAndMeasurementOfNewCablesForLights',
            'ConformNen3140AppendixJ',
            'ConformNTA8220Tabel1',
            'Other',
        ] as const,
        electricalInspectionIntervalDeterminationKindEnum: [
            'ToBeDeterminedDuringInspection',
            'DictatedByInsuranceCompany',
            'NTA8220',
            'Other',
        ] as const,
        electricalPowerShutdownMethodKindEnum: [
            'ConformDocumentSuppliedByInstallationAdministrator',
            'CustomerPerformsPowerShutdown',
            'InspectionCompanyIsAuthorized',
            'CustomerDoesNotGivePermission',
            'Other',
        ] as const,
        electricalPVInstallationTypologyKindEnum: [
            'StringOrCentralInverter',
            'OptimizersWithInverters',
            'MicroInverters',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        shutdownMethodKind: z
            .enum([
                'ConformDocumentSuppliedByInstallationAdministrator',
                'CustomerPerformsPowerShutdown',
                'InspectionCompanyIsAuthorized',
                'CustomerDoesNotGivePermission',
                'Other',
            ])
            .nullable()
            .optional(),
        shutdownMethodOther: z.string().max(1000).nullable().optional(),
        shutdownMethodRemark: z.string().max(1000).nullable().optional(),
        pVInstallationTypologyKind: z
            .enum([
                'StringOrCentralInverter',
                'OptimizersWithInverters',
                'MicroInverters',
            ])
            .nullable()
            .optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionPlanNumber: z.string().max(100).nullable().optional(),
            inspectionPlanVersion: z.string().max(100).nullable().optional(),
            projectNumber: z.string().max(100).nullable().optional(),
            priceQuoteNumber: z.string().max(100).nullable().optional(),
            inspectionPlanCreatedByUserId: z.string().nullable().optional(),
            sampleMethodKind: z
                .enum([
                    'ConformNen3140AppendixJOrNTA8220Tabel1',
                    'AdditionalInstruction',
                    'Complete',
                    'LimitToFirstSampleRound',
                    'SampleCanBeExpandedBasedOnAppendixJ',
                    'TestingAndMeasurementOfNewCablesForLights',
                    'ConformNen3140AppendixJ',
                    'ConformNTA8220Tabel1',
                    'Other',
                ])
                .nullable()
                .optional(),
            sampleMethodOther: z.string().max(1000).nullable().optional(),
            sampleMethodRemark: z.string().max(1000).nullable().optional(),
            intervalDeterminationKind: z
                .enum([
                    'ToBeDeterminedDuringInspection',
                    'DictatedByInsuranceCompany',
                    'NTA8220',
                    'Other',
                ])
                .nullable()
                .optional(),
            nextInspectionInYears: z
                .number()
                .min(0)
                .max(100)
                .nullable()
                .optional(),
            intervalDeterminationOther: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            intervalDeterminationRemark: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            shutdownMethodKind: z
                .enum([
                    'ConformDocumentSuppliedByInstallationAdministrator',
                    'CustomerPerformsPowerShutdown',
                    'InspectionCompanyIsAuthorized',
                    'CustomerDoesNotGivePermission',
                    'Other',
                ])
                .nullable()
                .optional(),
            shutdownMethodOther: z.string().max(1000).nullable().optional(),
            shutdownMethodRemark: z.string().max(1000).nullable().optional(),
            pVInstallationTypologyKind: z
                .enum([
                    'StringOrCentralInverter',
                    'OptimizersWithInverters',
                    'MicroInverters',
                ])
                .nullable()
                .optional(),
            pVInspectionCoverEntireInstallation: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationExludedParts: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectInspectionPlanRemarkCommand: {
        electricalProjectInspectionPlanRemarkId: z.string().min(1),
        order: z.number(),
        subject: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectInspectionPlanRemarkId: z.string().min(1),
            order: z.number(),
            subject: z.string().max(100).nullable().optional(),
            remark: z.string().max(1000).nullable().optional(),
        }),
    },
    editElectricalProjectInspectionPlanReportCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalInspectionSampleMethodKindEnum: [
            'ConformNen3140AppendixJOrNTA8220Tabel1',
            'AdditionalInstruction',
            'Complete',
            'LimitToFirstSampleRound',
            'SampleCanBeExpandedBasedOnAppendixJ',
            'TestingAndMeasurementOfNewCablesForLights',
            'ConformNen3140AppendixJ',
            'ConformNTA8220Tabel1',
            'Other',
        ] as const,
        electricalInspectionIntervalDeterminationKindEnum: [
            'ToBeDeterminedDuringInspection',
            'DictatedByInsuranceCompany',
            'NTA8220',
            'Other',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        enabled: z.boolean(),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean(),
            inspectionPlanNumber: z.string().max(100).nullable().optional(),
            inspectionPlanVersion: z.string().max(100).nullable().optional(),
            projectNumber: z.string().max(100).nullable().optional(),
            priceQuoteNumber: z.string().max(100).nullable().optional(),
            inspectionPlanCreatedByUserId: z.string().nullable().optional(),
            sampleMethodKind: z
                .enum([
                    'ConformNen3140AppendixJOrNTA8220Tabel1',
                    'AdditionalInstruction',
                    'Complete',
                    'LimitToFirstSampleRound',
                    'SampleCanBeExpandedBasedOnAppendixJ',
                    'TestingAndMeasurementOfNewCablesForLights',
                    'ConformNen3140AppendixJ',
                    'ConformNTA8220Tabel1',
                    'Other',
                ])
                .nullable()
                .optional(),
            sampleMethodOther: z.string().max(1000).nullable().optional(),
            sampleMethodRemark: z.string().max(1000).nullable().optional(),
            intervalDeterminationKind: z
                .enum([
                    'ToBeDeterminedDuringInspection',
                    'DictatedByInsuranceCompany',
                    'NTA8220',
                    'Other',
                ])
                .nullable()
                .optional(),
            nextInspectionInYears: z
                .number()
                .min(0)
                .max(100)
                .nullable()
                .optional(),
            intervalDeterminationOther: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            intervalDeterminationRemark: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectInspectionScheduleCommand: {
        electricalProjectVersionId: z.string().min(1),
        isOrderConfirmed: z.boolean().nullable().optional(),
        orderConfirmationRemark: z.string().max(500).nullable().optional(),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            isOrderConfirmed: z.boolean().nullable().optional(),
            orderConfirmationRemark: z.string().max(500).nullable().optional(),
            startDate: z.date().nullable().optional(),
            endDate: z.date().nullable().optional(),
        }),
    },
    editElectricalProjectMeasurementDeviceCommand: {
        electricalProjectMeasurementDeviceId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectMeasurementDeviceId: z.string().min(1),
        }),
    },
    editElectricalProjectObjectAddressCommand: {
        electricalProjectVersionId: z.string().min(1),
        street: z.string().min(1).max(64),
        streetNumber: z
            .string()
            .min(1)
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
        postalCode: z
            .string()
            .min(1)
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
        locality: z.string().min(1).max(64),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            street: z.string().min(1).max(64),
            streetNumber: z
                .string()
                .min(1)
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
            postalCode: z
                .string()
                .min(1)
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
            locality: z.string().min(1).max(64),
        }),
    },
    editElectricalProjectObjectCommand: {
        electricalProjectInspectionPlanCreatorKindEnum: [
            'InstallationAdministrator',
            'BuildingOwnerOrAdministrator',
            'Customer',
            'InspectionCompany',
        ] as const,
        electricalPVInstallationBuildKindEnum: [
            'InRoof',
            'OnRoof',
            'OnField',
            'OnWater',
        ] as const,
        electricalPVInstallationSpecialSpacesKindEnum: [
            'New',
            'Existing',
            'Expanded',
        ] as const,
        electricalEarthingSystemKindEnum: [
            'TT',
            'TN-S',
            'TN-C',
            'TN-CS',
            'IT',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        bagIdentificationNumber: z.string().max(30).nullable().optional(),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        street: z.string().max(64).nullable().optional(),
        streetNumber: z
            .string()
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
            .nullable()
            .optional(),
        postalCode: z
            .string()
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
            .nullable()
            .optional(),
        locality: z.string().max(64).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        buildingConstructionYear: z
            .number()
            .min(1000)
            .max(2100)
            .nullable()
            .optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        buildingCount: z.number().min(0).max(100000).nullable().optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(100000).nullable().optional(),
        isElectricalInstallationConstructionYearKnown: z
            .boolean()
            .nullable()
            .optional(),
        electricalInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        remarks: z.string().max(1000).nullable().optional(),
        inspectionPlanCreatorKind: z
            .enum([
                'InstallationAdministrator',
                'BuildingOwnerOrAdministrator',
                'Customer',
                'InspectionCompany',
            ])
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
        pVInverterCount: z.number().min(0).max(100000).nullable().optional(),
        pVPanelsTotalPower: z.number().min(0).max(10000).nullable().optional(),
        pVInvertersTotalPower: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pVBuildKind: z
            .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
            .nullable()
            .optional(),
        pVInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        pVSpecialSpaceKind: z
            .enum(['New', 'Existing', 'Expanded'])
            .nullable()
            .optional(),
        pVEarthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
        pVInstallationHasLightningProtection: z.boolean().nullable().optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            bagIdentificationNumber: z.string().max(30).nullable().optional(),
            name: z.string().max(100).nullable().optional(),
            description: z.string().max(250).nullable().optional(),
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            totalFloorArea: z
                .number()
                .min(0)
                .max(1000000)
                .nullable()
                .optional(),
            buildingConstructionYear: z
                .number()
                .min(1000)
                .max(2100)
                .nullable()
                .optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            buildingCount: z.number().min(0).max(100000).nullable().optional(),
            distributionBoardCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            controlBoxCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            isElectricalInstallationConstructionYearKnown: z
                .boolean()
                .nullable()
                .optional(),
            electricalInstallationConstructionYear: z
                .number()
                .min(1800)
                .max(2100)
                .nullable()
                .optional(),
            remarks: z.string().max(1000).nullable().optional(),
            inspectionPlanCreatorKind: z
                .enum([
                    'InstallationAdministrator',
                    'BuildingOwnerOrAdministrator',
                    'Customer',
                    'InspectionCompany',
                ])
                .nullable()
                .optional(),
            pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
            pVInverterCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            pVPanelsTotalPower: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pVInvertersTotalPower: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pVBuildKind: z
                .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
                .nullable()
                .optional(),
            pVInstallationConstructionYear: z
                .number()
                .min(1800)
                .max(2100)
                .nullable()
                .optional(),
            pVSpecialSpaceKind: z
                .enum(['New', 'Existing', 'Expanded'])
                .nullable()
                .optional(),
            pVEarthingSystemKind: z
                .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
                .nullable()
                .optional(),
            pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
            pVInstallationHasLightningProtection: z
                .boolean()
                .nullable()
                .optional(),
            pVInspectionCoverEntireInstallation: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationExludedParts: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectObservationCommand: {
        editElectricalProjectObservationCommandReport: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalProjectObservationReportTextKindEnum: [
                'None',
                'Same',
                'Custom',
            ] as const,
            electricalObservationClassificationCodeKindEnum: [
                'Severe',
                'Serious',
                'Minor',
                'Remark',
            ] as const,
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean(),
            remarkKind: z.enum(['None', 'Same', 'Custom']),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
            bodySchema: z.object({
                inspectionKind: z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ]),
                enabled: z.boolean(),
                remarkKind: z.enum(['None', 'Same', 'Custom']),
                remark: z.string().max(1000).nullable().optional(),
                recommendation: z.string().max(1000).nullable().optional(),
                electricalObservationClassificationGroupId: z
                    .string()
                    .nullable()
                    .optional(),
                electricalObservationClassificationCodeId: z
                    .string()
                    .nullable()
                    .optional(),
                codeKind: z
                    .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                    .nullable()
                    .optional(),
            }),
        },
        electricalObservationClassificationCodeKindEnum: [
            'Severe',
            'Serious',
            'Minor',
            'Remark',
        ] as const,
        electricalProjectObservationId: z.string().min(1),
        order: z.number(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            order: z.number(),
            floorplanElectricalProjectFileId: z.string().nullable().optional(),
            floorplanPinCoordinateX: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            floorplanPinCoordinateY: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            locationRemark: z.string().max(100).nullable().optional(),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectOrganizationCommand: {
        editElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
            bodySchema: z.object({
                street: z.string().max(64).nullable().optional(),
                streetNumber: z
                    .string()
                    .max(32)
                    .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                    .nullable()
                    .optional(),
                postalCode: z
                    .string()
                    .max(16)
                    .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                    .nullable()
                    .optional(),
                locality: z.string().max(64).nullable().optional(),
                administrativeArea: z.string().max(64).nullable().optional(),
                countryId: z.string().nullable().optional(),
            }),
        },
        electricalProjectOrganizationId: z.string().min(1),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean(),
        isObjectOwner: z.boolean(),
        isObjectAdministrator: z.boolean(),
        isObjectTenant: z.boolean(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectOrganizationId: z.string().min(1),
            name: z.string().max(100).nullable().optional(),
            description: z.string().max(250).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            website: z.string().max(250).nullable().optional(),
            isProjectCustomer: z.boolean(),
            isObjectOwner: z.boolean(),
            isObjectAdministrator: z.boolean(),
            isObjectTenant: z.boolean(),
            isPVInstallationConstructionCompany: z
                .boolean()
                .nullable()
                .optional(),
        }),
    },
    editElectricalProjectPriceQuoteCommand: {
        electricalProjectVersionId: z.string().min(1),
        outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
        basePrice: z.number().min(0).max(100000).nullable().optional(),
        squareMeterCount: z.number().min(0).max(10000000).nullable().optional(),
        pricePerSquareMeter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pricePerDistributionBoard: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerControlBox: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRemoteRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        discountPercentage: z.number().min(0).max(25).nullable().optional(),
        pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVInverter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVPanel: z.number().min(0).max(10000000).nullable().optional(),
        priceQuoteDate: z.date().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
            basePrice: z.number().min(0).max(100000).nullable().optional(),
            squareMeterCount: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerSquareMeter: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            distributionBoardCount: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pricePerDistributionBoard: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerControlBox: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerRepeatInspection: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerRemoteRepeatInspection: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            discountPercentage: z.number().min(0).max(25).nullable().optional(),
            pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerPVInverter: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerPVPanel: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            priceQuoteDate: z.date().nullable().optional(),
        }),
    },
    editElectricalProjectPVInverterCommand: {
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalProjectPVInverterId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVInverterId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            brandModel: z.string().max(100).nullable().optional(),
            serialNumber: z.string().max(100).nullable().optional(),
            firmwareVersion: z.string().max(100).nullable().optional(),
            ratedPower: z.number().min(0).max(100000).nullable().optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            order: z.number(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            groundImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportImpedanceMeasurements: z.boolean().nullable().optional(),
            reportPVStrings: z.boolean().nullable().optional(),
        }),
    },
    editElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalProjectPVStringId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            voltage: z.number().min(0).max(100000).nullable().optional(),
            resistance: z.number().min(0).max(100000).nullable().optional(),
            current: z.number().min(0).max(100000).nullable().optional(),
            order: z.number(),
        }),
    },
    editElectricalProjectRisoMeasurementCommand: {
        electricalRisoMeasurementCircuitKindEnum: [
            'L-PE / N-PE',
            'L1-L2',
            'L2-L3',
            'L1-L3',
            'L1-N',
            'L2-N',
            'L3-N',
            'L1-PE',
            'L2-PE',
            'L3-PE',
            'N-PE',
            'L-PE',
        ] as const,
        electricalProjectRisoMeasurementId: z.string().min(1),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalProjectRisoMeasurementId: z.string().min(1),
            circuitKind: z
                .enum([
                    'L-PE / N-PE',
                    'L1-L2',
                    'L2-L3',
                    'L1-L3',
                    'L1-N',
                    'L2-N',
                    'L3-N',
                    'L1-PE',
                    'L2-PE',
                    'L3-PE',
                    'N-PE',
                    'L-PE',
                ])
                .nullable()
                .optional(),
            measuredValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            minimumValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isFaulty: z.boolean().nullable().optional(),
            order: z.number(),
        }),
    },
    editElectricalProjectSelectListItemCommand: {
        editElectricalProjectSelectListItemCommandItem: {
            electricalProjectSelectListItemId: z.string().min(1),
            selected: z.boolean().nullable().optional(),
            customValue: z.string().max(500).nullable().optional(),
            bodySchema: z.object({
                electricalProjectSelectListItemId: z.string().min(1),
                selected: z.boolean().nullable().optional(),
                customValue: z.string().max(500).nullable().optional(),
            }),
        },
        electricalProjectSelectListKindEnum: [
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        listKind: z.enum([
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ]),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            listKind: z.enum([
                'InspectionExclusion',
                'ObjectSpecialSpace',
                'AdditionalInstruction',
                'AdditionalTopic',
                'InspectionComponent',
                'BuildingFunction',
            ]),
        }),
    },
    editElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().min(1),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().min(1).max(200),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalProjectTechnicalDrawingId: z.string().min(1),
            isDrawingPresent: z.boolean().nullable().optional(),
            electricalProjectFileId: z.string().nullable().optional(),
            name: z.string().min(1).max(200),
            location: z.string().max(200).nullable().optional(),
            description: z.string().max(500).nullable().optional(),
            order: z.number(),
        }),
    },
    editElectricalProjectUserCommand: {
        electricalProjectUserKindEnum: [
            'Inspector',
            'CertifiedInspector',
            'PriceQuoteSigner',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        userId: z.string().min(1),
        kind: z.enum(['Inspector', 'CertifiedInspector', 'PriceQuoteSigner']),
        selected: z.boolean(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            userId: z.string().min(1),
            kind: z.enum([
                'Inspector',
                'CertifiedInspector',
                'PriceQuoteSigner',
            ]),
            selected: z.boolean(),
        }),
    },
    editObjectCommand: {
        objectId: z.string().min(1),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        constructionYear: z.number().min(1000).max(2100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            objectId: z.string().min(1),
            name: z.string().max(100).nullable().optional(),
            description: z.string().max(250).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            totalFloorArea: z
                .number()
                .min(0)
                .max(1000000)
                .nullable()
                .optional(),
            constructionYear: z
                .number()
                .min(1000)
                .max(2100)
                .nullable()
                .optional(),
            remarks: z.string().max(1000).nullable().optional(),
        }),
    },
    editObjectRflowLinkCommand: {
        objectRflowLinkId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            objectRflowLinkId: z.string().min(1),
            enabled: z.boolean(),
            linkAutomatically: z.boolean().nullable().optional(),
            remarks: z.string().max(500).nullable().optional(),
        }),
    },
    editOrganizationCommand: {
        organizationId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        slogan: z.string().max(250).nullable().optional(),
        chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        iBAN: z.string().max(40).nullable().optional(),
        bIC: z.string().max(15).nullable().optional(),
        vatNumber: z.string().max(30).nullable().optional(),
        sciosRegistrationNumber: z.string().max(30).nullable().optional(),
        enableElectricalProjectPriceQuote: z.boolean().nullable().optional(),
        enableElectricalProjectPlanning: z.boolean().nullable().optional(),
        enableElectricalProjectRecommendations: z
            .boolean()
            .nullable()
            .optional(),
        bodySchema: z.object({
            organizationId: z.string().min(1),
            name: z.string().min(1).max(100),
            description: z.string().max(250).nullable().optional(),
            slogan: z.string().max(250).nullable().optional(),
            chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            website: z.string().max(250).nullable().optional(),
            iBAN: z.string().max(40).nullable().optional(),
            bIC: z.string().max(15).nullable().optional(),
            vatNumber: z.string().max(30).nullable().optional(),
            sciosRegistrationNumber: z.string().max(30).nullable().optional(),
            enableElectricalProjectPriceQuote: z
                .boolean()
                .nullable()
                .optional(),
            enableElectricalProjectPlanning: z.boolean().nullable().optional(),
            enableElectricalProjectRecommendations: z
                .boolean()
                .nullable()
                .optional(),
        }),
    },
    editOrganizationFileCommand: {
        organizationFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
        bodySchema: z.object({
            organizationFileId: z.string().min(1),
            order: z.number(),
            description: z.string().max(200).nullable().optional(),
        }),
    },
    editUserFileCommand: {
        userFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
        bodySchema: z.object({
            userFileId: z.string().min(1),
            order: z.number(),
            description: z.string().max(200).nullable().optional(),
        }),
    },
    editUserSettingsCommand: {
        userId: z.string().min(1),
        displayName: z.string().max(50).nullable().optional(),
        signatureText: z.string().max(50).nullable().optional(),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        phoneNumber: z.string().max(60).nullable().optional(),
        colorCode: z.string().max(30).nullable().optional(),
        bodySchema: z.object({
            userId: z.string().min(1),
            displayName: z.string().max(50).nullable().optional(),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
        }),
    },
    forgotAccountPasswordCommand: {
        email: z.union([z.literal(''), z.string().email()]),
        bodySchema: z.object({
            email: z.union([z.literal(''), z.string().email()]),
        }),
    },
    listKVKRegistrationQuery: {
        listKVKRegistrationQueryKindEnum: [
            'Environment',
            'ElectricalProject',
        ] as const,
        objectKind: z.enum(['Environment', 'ElectricalProject']),
        objectId: z.string().min(1),
        kVKNumber: z
            .string()
            .min(8)
            .max(8)
            .regex(/^[0-9]{8}$/)
            .nullable()
            .optional(),
        branchNumber: z
            .string()
            .min(12)
            .max(12)
            .regex(/^[0-9]{12}$/)
            .nullable()
            .optional(),
        name: z.string().max(200).nullable().optional(),
        street: z.string().max(200).nullable().optional(),
        locality: z.string().max(200).nullable().optional(),
        postalCode: z
            .string()
            .min(6)
            .max(6)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
            .nullable()
            .optional(),
        streetNumber: z.number().nullable().optional(),
        streetNumberSuffix: z
            .string()
            .min(1)
            .max(1)
            .regex(/^[A-Za-z]{1}$/)
            .nullable()
            .optional(),
        postalBoxNumber: z.number().min(0).max(99999).nullable().optional(),
        pageNumber: z.number().min(1).max(1000),
        pageSize: z.number().min(1).max(100),
        bodySchema: z.object({
            objectKind: z.enum(['Environment', 'ElectricalProject']),
            objectId: z.string().min(1),
            kVKNumber: z
                .string()
                .min(8)
                .max(8)
                .regex(/^[0-9]{8}$/)
                .nullable()
                .optional(),
            branchNumber: z
                .string()
                .min(12)
                .max(12)
                .regex(/^[0-9]{12}$/)
                .nullable()
                .optional(),
            name: z.string().max(200).nullable().optional(),
            street: z.string().max(200).nullable().optional(),
            locality: z.string().max(200).nullable().optional(),
            postalCode: z
                .string()
                .min(6)
                .max(6)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            streetNumber: z.number().nullable().optional(),
            streetNumberSuffix: z
                .string()
                .min(1)
                .max(1)
                .regex(/^[A-Za-z]{1}$/)
                .nullable()
                .optional(),
            postalBoxNumber: z.number().min(0).max(99999).nullable().optional(),
            pageNumber: z.number().min(1).max(1000),
            pageSize: z.number().min(1).max(100),
        }),
    },
    oAuth2TokenV2Request: {
        grant_type: z.string().min(1),
        username: z.string().min(1),
        password: z.string().min(100),
        bodySchema: z.object({
            grant_type: z.string().min(1),
            username: z.string().min(1),
            password: z.string().min(100),
        }),
    },
    oAuth2TokenV2ResponseBadRequest: {
        error: z.string().nullable().optional(),
        error_description: z.string().nullable().optional(),
        error_uri: z.string().nullable().optional(),
        bodySchema: z.object({
            error: z.string().nullable().optional(),
            error_description: z.string().nullable().optional(),
            error_uri: z.string().nullable().optional(),
        }),
    },
    patchElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectContactPersonId: z.string().min(1),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            mobilePhoneNumber: z.string().max(60).nullable().optional(),
            receivePriceQuote: z.boolean().nullable().optional(),
            receiveInvoice: z.boolean().nullable().optional(),
            receiveInspectionPlan: z.boolean().nullable().optional(),
            receiveInspectionReport: z.boolean().nullable().optional(),
            receivePowerShutdownPlan: z.boolean().nullable().optional(),
            isResponsibleForPowerSystem: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardKindEnum: [
            'MainDistributionBoard',
            'SubDistributionBoard',
            'ControlBox',
        ] as const,
        electricalEarthingSystemKindEnum: [
            'TT',
            'TN-S',
            'TN-C',
            'TN-CS',
            'IT',
        ] as const,
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalApplicanceProtectionClassEnum: [
            '0',
            '0I',
            'I',
            'II',
            'IIFE',
            'III',
        ] as const,
        electricalDistributionBoardUpstreamProtectiveDeviceKindEnum: [
            'DiazedFuse',
            'FuseLink',
            'CircuitBreaker',
            'Unknown',
            'PowerCircuitBreaker',
        ] as const,
        electricalPowerPhaseRotationEnum: [
            'Clockwise',
            'CounterClockwise',
            'NotApplicable',
        ] as const,
        electricalProjectDistributionBoardId: z.string().min(1),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        parentElectricalProjectDistributionBoardId: z
            .string()
            .nullable()
            .optional(),
        order: z.number().nullable().optional(),
        identifier: z.string().max(100).nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        earthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        electricalIngressProtectionRatingId: z.string().nullable().optional(),
        customElectricalIngressProtectionRating: z
            .string()
            .nullable()
            .optional(),
        applicanceProtectionClass: z
            .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
            .nullable()
            .optional(),
        mainSwitchNominalCurrent: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        upstreamProtectiveDeviceKind: z
            .enum([
                'DiazedFuse',
                'FuseLink',
                'CircuitBreaker',
                'Unknown',
                'PowerCircuitBreaker',
            ])
            .nullable()
            .optional(),
        upstreamProtectiveDeviceCharacteristic: z
            .string()
            .max(50)
            .nullable()
            .optional(),
        powerCircuitBreakerType: z.string().max(100).nullable().optional(),
        powerCircuitBreakerSetting: z.string().max(100).nullable().optional(),
        powerCircuitBreakerCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentNeutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageNeutralGround: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        powerPhaseRotation: z
            .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
            .nullable()
            .optional(),
        groundImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        measureImpedanceBetweenPowerPhases: z.boolean().nullable().optional(),
        impedanceCurrentL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardId: z.string().min(1),
            kind: z
                .enum([
                    'MainDistributionBoard',
                    'SubDistributionBoard',
                    'ControlBox',
                ])
                .nullable()
                .optional(),
            isPVInstallation: z.boolean().nullable().optional(),
            parentElectricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            order: z.number().nullable().optional(),
            identifier: z.string().max(100).nullable().optional(),
            floorNumber: z.string().max(50).nullable().optional(),
            spaceNumber: z.string().max(50).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            earthingSystemKind: z
                .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
                .nullable()
                .optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            electricalIngressProtectionRatingId: z
                .string()
                .nullable()
                .optional(),
            customElectricalIngressProtectionRating: z
                .string()
                .nullable()
                .optional(),
            applicanceProtectionClass: z
                .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
                .nullable()
                .optional(),
            mainSwitchNominalCurrent: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            upstreamProtectiveDeviceKind: z
                .enum([
                    'DiazedFuse',
                    'FuseLink',
                    'CircuitBreaker',
                    'Unknown',
                    'PowerCircuitBreaker',
                ])
                .nullable()
                .optional(),
            upstreamProtectiveDeviceCharacteristic: z
                .string()
                .max(50)
                .nullable()
                .optional(),
            powerCircuitBreakerType: z.string().max(100).nullable().optional(),
            powerCircuitBreakerSetting: z
                .string()
                .max(100)
                .nullable()
                .optional(),
            powerCircuitBreakerCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            currentNeutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3Neutral: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL1Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL2Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageL3Ground: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            voltageNeutralGround: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            powerPhaseRotation: z
                .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
                .nullable()
                .optional(),
            groundImpedanceCurrentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceCurrentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceCurrentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceCurrentL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            measureImpedanceBetweenPowerPhases: z
                .boolean()
                .nullable()
                .optional(),
            impedanceCurrentL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceCurrentL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceCurrentL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCurrentMeasurements: z.boolean().nullable().optional(),
            reportVoltageMeasurements: z.boolean().nullable().optional(),
            reportGroundImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportNeutralImpedanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportResidualCurrentDevices: z.boolean().nullable().optional(),
            reportResidualCurrentCircuitBreakerDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportFinalCircuitProtectiveDevices: z
                .boolean()
                .nullable()
                .optional(),
            reportIsolationResistanceMeasurements: z
                .boolean()
                .nullable()
                .optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectDistributionBoardDeviceCommand: {
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalRisoValueSignEnum: ['<', '>', '='] as const,
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z.number().nullable().optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z.number().nullable().optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z.number().nullable().optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardDeviceId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number().nullable().optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            residualCurrentDeviceType: z.string().max(50).nullable().optional(),
            ratedCurrent: z.number().nullable().optional(),
            tripDelay: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ratedResidualCurrent: z.number().nullable().optional(),
            fuseKind: z.string().max(50).nullable().optional(),
            trippingCharacteristic: z.string().max(50).nullable().optional(),
            breakerRatedCurrent: z.number().nullable().optional(),
            isTestButtonFunctional: z.boolean().nullable().optional(),
            resistanceL1PE: z.number().nullable().optional(),
            resistanceL2PE: z.number().nullable().optional(),
            resistanceL3PE: z.number().nullable().optional(),
            resistanceNeutralPE: z.number().nullable().optional(),
            resistanceL1Neutral: z.number().nullable().optional(),
            resistanceL2Neutral: z.number().nullable().optional(),
            resistanceL3Neutral: z.number().nullable().optional(),
            measureResistanceBetweenPowerPhases: z
                .boolean()
                .nullable()
                .optional(),
            resistanceL1L2: z.number().nullable().optional(),
            resistanceL1L3: z.number().nullable().optional(),
            resistanceL2L3: z.number().nullable().optional(),
            risoSign: z.enum(['<', '>', '=']).nullable().optional(),
            risoValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            ziMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsResistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            zsMinimalCurrent: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportRisoMeasurements: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileId: z.string().min(1),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number().nullable().optional(),
        remark: z.string().max(100).nullable().optional(),
        bodySchema: z.object({
            electricalProjectDistributionBoardFileId: z.string().min(1),
            electricalProjectFileId: z.string().nullable().optional(),
            order: z.number().nullable().optional(),
            remark: z.string().max(100).nullable().optional(),
        }),
    },
    patchElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z.string().min(1),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number(),
        bodySchema: z.object({
            electricalProjectEquipotentialBondingMeasurementId: z
                .string()
                .min(1),
            location: z.string().max(100).nullable().optional(),
            resistance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            order: z.number(),
        }),
    },
    patchElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitKindEnum: [
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Space',
            'Building',
            'Other',
        ] as const,
        electricalProjectFinalCircuitId: z.string().min(1),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectFinalCircuitId: z.string().min(1),
            kind: z
                .enum([
                    'FinalCircuit',
                    'PhotovoltaicInverter',
                    'Machine',
                    'Space',
                    'Building',
                    'Other',
                ])
                .nullable()
                .optional(),
            identifier: z.string().max(100).nullable().optional(),
            order: z.number().nullable().optional(),
            floorNumber: z.string().max(50).nullable().optional(),
            spaceNumber: z.string().max(50).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportCheckListItems: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectInspectionCommand: {
        electricalProjectInspectionKindEnum: [
            'Inspection',
            'RepeatInspection',
        ] as const,
        electricalProjectDistributionBoardMeasurementRegistrationKindEnum: [
            'Input',
            'Attachment',
            'Request',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z
            .enum(['Inspection', 'RepeatInspection'])
            .nullable()
            .optional(),
        reportNumber: z.string().max(100).nullable().optional(),
        distributionBoardMeasurementRegistrationKind: z
            .enum(['Input', 'Attachment', 'Request'])
            .nullable()
            .optional(),
        enableExtendedRisoOnDistributionBoardDevices: z
            .boolean()
            .nullable()
            .optional(),
        pVSolarIrradiance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        pVSolarPanelTemperature: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isPVInstallationEquipotentialBondingPassed: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationEquipotentialBondingPassedRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isPVInstallationConstructionCompanyKnown: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationConstructionDocumentNumber: z
            .string()
            .max(100)
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z
                .enum(['Inspection', 'RepeatInspection'])
                .nullable()
                .optional(),
            reportNumber: z.string().max(100).nullable().optional(),
            distributionBoardMeasurementRegistrationKind: z
                .enum(['Input', 'Attachment', 'Request'])
                .nullable()
                .optional(),
            enableExtendedRisoOnDistributionBoardDevices: z
                .boolean()
                .nullable()
                .optional(),
            pVSolarIrradiance: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            pVSolarPanelTemperature: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isPVInstallationEquipotentialBondingPassed: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationEquipotentialBondingPassedRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isPVInstallationConstructionCompanyKnown: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationConstructionDocumentNumber: z
                .string()
                .max(100)
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectInspectionConclusionCommand: {
        patchElectricalProjectInspectionConclusionCommandScope: {
            sciosInspectionCode: z.string().max(20).nullable().optional(),
            bodySchema: z.object({
                sciosInspectionCode: z.string().max(20).nullable().optional(),
            }),
        },
        patchElectricalProjectInspectionConclusionCommandIntervalQuestions: {
            electricalInspectionDetermineIntervalKindEnum: [
                'Age1',
                'Age2',
                'Age3',
                'Age4',
                'Quality1',
                'Quality2',
                'Quality3',
                'Quality4',
                'Quality5',
                'Environment1',
                'Environment2',
                'Environment3',
                'Users1',
                'Users2',
                'Users3',
                'Users4',
                'Supervision1',
                'Supervision2',
            ] as const,
            determineInspectionIntervalAge: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            bodySchema: z.object({
                determineInspectionIntervalAge: z
                    .enum([
                        'Age1',
                        'Age2',
                        'Age3',
                        'Age4',
                        'Quality1',
                        'Quality2',
                        'Quality3',
                        'Quality4',
                        'Quality5',
                        'Environment1',
                        'Environment2',
                        'Environment3',
                        'Users1',
                        'Users2',
                        'Users3',
                        'Users4',
                        'Supervision1',
                        'Supervision2',
                    ])
                    .nullable()
                    .optional(),
            }),
        },
        electricalInspectionIntervalDeterminedByKindEnum: [
            'InsuranceCompany',
            'Inspection',
            'RentalContract',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        scope10InspectionIntervalTableCellId: z.number().nullable().optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            nextInspectionInYearCount: z
                .number()
                .min(0)
                .max(15)
                .nullable()
                .optional(),
            scope10InspectionIntervalTableCellId: z
                .number()
                .nullable()
                .optional(),
            intervalDeterminedByKind: z
                .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
                .nullable()
                .optional(),
            includeRepairStatement: z.boolean().nullable().optional(),
            isPerformedAccordingToInspectionPlan: z
                .boolean()
                .nullable()
                .optional(),
            performedAccordingToInspectionPlanRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isFinalVerdictPassed: z.boolean().nullable().optional(),
            finalVerdictRemark: z.string().max(500).nullable().optional(),
        }),
    },
    patchElectricalProjectInspectionConclusionReportCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalInspectionIntervalDeterminedByKindEnum: [
            'InsuranceCompany',
            'Inspection',
            'RentalContract',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        sciosInspectionCode: z.string().max(20).nullable().optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            nextInspectionInYearCount: z
                .number()
                .min(0)
                .max(15)
                .nullable()
                .optional(),
            intervalDeterminedByKind: z
                .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
                .nullable()
                .optional(),
            sciosInspectionCode: z.string().max(20).nullable().optional(),
            includeRepairStatement: z.boolean().nullable().optional(),
            isPerformedAccordingToInspectionPlan: z
                .boolean()
                .nullable()
                .optional(),
            performedAccordingToInspectionPlanRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            isFinalVerdictPassed: z.boolean().nullable().optional(),
            finalVerdictRemark: z.string().max(500).nullable().optional(),
        }),
    },
    patchElectricalProjectInspectionNormCommand: {
        electricalProjectInspectionNormStatusEnum: [
            'Selected',
            'NotSelected',
            'NotApplicable',
        ] as const,
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalProjectInspectionNormId: z.string().min(1),
        status: z
            .enum(['Selected', 'NotSelected', 'NotApplicable'])
            .nullable()
            .optional(),
        addInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
        customDescription: z.string().max(1000).nullable().optional(),
        version: z.string().max(100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectInspectionNormId: z.string().min(1),
            status: z
                .enum(['Selected', 'NotSelected', 'NotApplicable'])
                .nullable()
                .optional(),
            addInspectionKind: z
                .enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
                .nullable()
                .optional(),
            customDescription: z.string().max(1000).nullable().optional(),
            version: z.string().max(100).nullable().optional(),
            remarks: z.string().max(1000).nullable().optional(),
        }),
    },
    patchElectricalProjectInspectionPlanCommand: {
        electricalInspectionSampleMethodKindEnum: [
            'ConformNen3140AppendixJOrNTA8220Tabel1',
            'AdditionalInstruction',
            'Complete',
            'LimitToFirstSampleRound',
            'SampleCanBeExpandedBasedOnAppendixJ',
            'TestingAndMeasurementOfNewCablesForLights',
            'ConformNen3140AppendixJ',
            'ConformNTA8220Tabel1',
            'Other',
        ] as const,
        electricalInspectionIntervalDeterminationKindEnum: [
            'ToBeDeterminedDuringInspection',
            'DictatedByInsuranceCompany',
            'NTA8220',
            'Other',
        ] as const,
        electricalPowerShutdownMethodKindEnum: [
            'ConformDocumentSuppliedByInstallationAdministrator',
            'CustomerPerformsPowerShutdown',
            'InspectionCompanyIsAuthorized',
            'CustomerDoesNotGivePermission',
            'Other',
        ] as const,
        electricalPVInstallationTypologyKindEnum: [
            'StringOrCentralInverter',
            'OptimizersWithInverters',
            'MicroInverters',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        shutdownMethodKind: z
            .enum([
                'ConformDocumentSuppliedByInstallationAdministrator',
                'CustomerPerformsPowerShutdown',
                'InspectionCompanyIsAuthorized',
                'CustomerDoesNotGivePermission',
                'Other',
            ])
            .nullable()
            .optional(),
        shutdownMethodOther: z.string().max(1000).nullable().optional(),
        shutdownMethodRemark: z.string().max(1000).nullable().optional(),
        pVInstallationTypologyKind: z
            .enum([
                'StringOrCentralInverter',
                'OptimizersWithInverters',
                'MicroInverters',
            ])
            .nullable()
            .optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionPlanNumber: z.string().max(100).nullable().optional(),
            inspectionPlanVersion: z.string().max(100).nullable().optional(),
            projectNumber: z.string().max(100).nullable().optional(),
            priceQuoteNumber: z.string().max(100).nullable().optional(),
            inspectionPlanCreatedByUserId: z.string().nullable().optional(),
            sampleMethodKind: z
                .enum([
                    'ConformNen3140AppendixJOrNTA8220Tabel1',
                    'AdditionalInstruction',
                    'Complete',
                    'LimitToFirstSampleRound',
                    'SampleCanBeExpandedBasedOnAppendixJ',
                    'TestingAndMeasurementOfNewCablesForLights',
                    'ConformNen3140AppendixJ',
                    'ConformNTA8220Tabel1',
                    'Other',
                ])
                .nullable()
                .optional(),
            sampleMethodOther: z.string().max(1000).nullable().optional(),
            sampleMethodRemark: z.string().max(1000).nullable().optional(),
            intervalDeterminationKind: z
                .enum([
                    'ToBeDeterminedDuringInspection',
                    'DictatedByInsuranceCompany',
                    'NTA8220',
                    'Other',
                ])
                .nullable()
                .optional(),
            nextInspectionInYears: z
                .number()
                .min(0)
                .max(100)
                .nullable()
                .optional(),
            intervalDeterminationOther: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            intervalDeterminationRemark: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            shutdownMethodKind: z
                .enum([
                    'ConformDocumentSuppliedByInstallationAdministrator',
                    'CustomerPerformsPowerShutdown',
                    'InspectionCompanyIsAuthorized',
                    'CustomerDoesNotGivePermission',
                    'Other',
                ])
                .nullable()
                .optional(),
            shutdownMethodOther: z.string().max(1000).nullable().optional(),
            shutdownMethodRemark: z.string().max(1000).nullable().optional(),
            pVInstallationTypologyKind: z
                .enum([
                    'StringOrCentralInverter',
                    'OptimizersWithInverters',
                    'MicroInverters',
                ])
                .nullable()
                .optional(),
            pVInspectionCoverEntireInstallation: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationExludedParts: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectInspectionPlanReportCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalInspectionSampleMethodKindEnum: [
            'ConformNen3140AppendixJOrNTA8220Tabel1',
            'AdditionalInstruction',
            'Complete',
            'LimitToFirstSampleRound',
            'SampleCanBeExpandedBasedOnAppendixJ',
            'TestingAndMeasurementOfNewCablesForLights',
            'ConformNen3140AppendixJ',
            'ConformNTA8220Tabel1',
            'Other',
        ] as const,
        electricalInspectionIntervalDeterminationKindEnum: [
            'ToBeDeterminedDuringInspection',
            'DictatedByInsuranceCompany',
            'NTA8220',
            'Other',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        enabled: z.boolean().nullable().optional(),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean().nullable().optional(),
            inspectionPlanNumber: z.string().max(100).nullable().optional(),
            inspectionPlanVersion: z.string().max(100).nullable().optional(),
            projectNumber: z.string().max(100).nullable().optional(),
            priceQuoteNumber: z.string().max(100).nullable().optional(),
            inspectionPlanCreatedByUserId: z.string().nullable().optional(),
            sampleMethodKind: z
                .enum([
                    'ConformNen3140AppendixJOrNTA8220Tabel1',
                    'AdditionalInstruction',
                    'Complete',
                    'LimitToFirstSampleRound',
                    'SampleCanBeExpandedBasedOnAppendixJ',
                    'TestingAndMeasurementOfNewCablesForLights',
                    'ConformNen3140AppendixJ',
                    'ConformNTA8220Tabel1',
                    'Other',
                ])
                .nullable()
                .optional(),
            sampleMethodOther: z.string().max(1000).nullable().optional(),
            sampleMethodRemark: z.string().max(1000).nullable().optional(),
            intervalDeterminationKind: z
                .enum([
                    'ToBeDeterminedDuringInspection',
                    'DictatedByInsuranceCompany',
                    'NTA8220',
                    'Other',
                ])
                .nullable()
                .optional(),
            nextInspectionInYears: z
                .number()
                .min(0)
                .max(100)
                .nullable()
                .optional(),
            intervalDeterminationOther: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            intervalDeterminationRemark: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectInspectionScheduleCommand: {
        electricalProjectVersionId: z.string().min(1),
        isOrderConfirmed: z.boolean().nullable().optional(),
        orderConfirmationRemark: z.string().max(500).nullable().optional(),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            isOrderConfirmed: z.boolean().nullable().optional(),
            orderConfirmationRemark: z.string().max(500).nullable().optional(),
            startDate: z.date().nullable().optional(),
            endDate: z.date().nullable().optional(),
        }),
    },
    patchElectricalProjectMeasurementDeviceCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalProjectMeasurementDeviceId: z.string().min(1),
        addInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectMeasurementDeviceId: z.string().min(1),
            addInspectionKind: z
                .enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectObjectCommand: {
        electricalProjectInspectionPlanCreatorKindEnum: [
            'InstallationAdministrator',
            'BuildingOwnerOrAdministrator',
            'Customer',
            'InspectionCompany',
        ] as const,
        electricalPVInstallationBuildKindEnum: [
            'InRoof',
            'OnRoof',
            'OnField',
            'OnWater',
        ] as const,
        electricalPVInstallationSpecialSpacesKindEnum: [
            'New',
            'Existing',
            'Expanded',
        ] as const,
        electricalEarthingSystemKindEnum: [
            'TT',
            'TN-S',
            'TN-C',
            'TN-CS',
            'IT',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        bagIdentificationNumber: z.string().max(30).nullable().optional(),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        street: z.string().max(64).nullable().optional(),
        streetNumber: z
            .string()
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
            .nullable()
            .optional(),
        postalCode: z
            .string()
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
            .nullable()
            .optional(),
        locality: z.string().max(64).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        buildingConstructionYear: z
            .number()
            .min(1000)
            .max(2100)
            .nullable()
            .optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        buildingCount: z.number().min(0).max(100000).nullable().optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(100000).nullable().optional(),
        isElectricalInstallationConstructionYearKnown: z
            .boolean()
            .nullable()
            .optional(),
        electricalInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        remarks: z.string().max(1000).nullable().optional(),
        inspectionPlanCreatorKind: z
            .enum([
                'InstallationAdministrator',
                'BuildingOwnerOrAdministrator',
                'Customer',
                'InspectionCompany',
            ])
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
        pVInverterCount: z.number().min(0).max(100000).nullable().optional(),
        pVPanelsTotalPower: z.number().min(0).max(10000).nullable().optional(),
        pVInvertersTotalPower: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pVBuildKind: z
            .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
            .nullable()
            .optional(),
        pVInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        pVSpecialSpaceKind: z
            .enum(['New', 'Existing', 'Expanded'])
            .nullable()
            .optional(),
        pVEarthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
        pVInstallationHasLightningProtection: z.boolean().nullable().optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            bagIdentificationNumber: z.string().max(30).nullable().optional(),
            name: z.string().max(100).nullable().optional(),
            description: z.string().max(250).nullable().optional(),
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            totalFloorArea: z
                .number()
                .min(0)
                .max(1000000)
                .nullable()
                .optional(),
            buildingConstructionYear: z
                .number()
                .min(1000)
                .max(2100)
                .nullable()
                .optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            buildingCount: z.number().min(0).max(100000).nullable().optional(),
            distributionBoardCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            controlBoxCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            isElectricalInstallationConstructionYearKnown: z
                .boolean()
                .nullable()
                .optional(),
            electricalInstallationConstructionYear: z
                .number()
                .min(1800)
                .max(2100)
                .nullable()
                .optional(),
            remarks: z.string().max(1000).nullable().optional(),
            inspectionPlanCreatorKind: z
                .enum([
                    'InstallationAdministrator',
                    'BuildingOwnerOrAdministrator',
                    'Customer',
                    'InspectionCompany',
                ])
                .nullable()
                .optional(),
            pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
            pVInverterCount: z
                .number()
                .min(0)
                .max(100000)
                .nullable()
                .optional(),
            pVPanelsTotalPower: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pVInvertersTotalPower: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pVBuildKind: z
                .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
                .nullable()
                .optional(),
            pVInstallationConstructionYear: z
                .number()
                .min(1800)
                .max(2100)
                .nullable()
                .optional(),
            pVSpecialSpaceKind: z
                .enum(['New', 'Existing', 'Expanded'])
                .nullable()
                .optional(),
            pVEarthingSystemKind: z
                .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
                .nullable()
                .optional(),
            pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
            pVInstallationHasLightningProtection: z
                .boolean()
                .nullable()
                .optional(),
            pVInspectionCoverEntireInstallation: z
                .boolean()
                .nullable()
                .optional(),
            pVInstallationExludedParts: z
                .string()
                .max(1000)
                .nullable()
                .optional(),
            pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectObservationCommand: {
        electricalObservationClassificationCodeKindEnum: [
            'Severe',
            'Serious',
            'Minor',
            'Remark',
        ] as const,
        electricalProjectObservationId: z.string().min(1),
        order: z.number().nullable().optional(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            order: z.number().nullable().optional(),
            floorplanElectricalProjectFileId: z.string().nullable().optional(),
            floorplanPinCoordinateX: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            floorplanPinCoordinateY: z
                .number()
                .min(0)
                .max(1000)
                .nullable()
                .optional(),
            locationRemark: z.string().max(100).nullable().optional(),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectObservationReportCommand: {
        electricalInspectionKindEnum: [
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ] as const,
        electricalProjectObservationReportTextKindEnum: [
            'None',
            'Same',
            'Custom',
        ] as const,
        electricalObservationClassificationCodeKindEnum: [
            'Severe',
            'Serious',
            'Minor',
            'Remark',
        ] as const,
        electricalProjectObservationId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        enabled: z.boolean().nullable().optional(),
        remarkKind: z.enum(['None', 'Same', 'Custom']).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean().nullable().optional(),
            remarkKind: z
                .enum(['None', 'Same', 'Custom'])
                .nullable()
                .optional(),
            remark: z.string().max(1000).nullable().optional(),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectOrganizationCommand: {
        patchElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
            bodySchema: z.object({
                street: z.string().max(64).nullable().optional(),
                streetNumber: z
                    .string()
                    .max(32)
                    .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                    .nullable()
                    .optional(),
                postalCode: z
                    .string()
                    .max(16)
                    .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                    .nullable()
                    .optional(),
                locality: z.string().max(64).nullable().optional(),
                administrativeArea: z.string().max(64).nullable().optional(),
                countryId: z.string().nullable().optional(),
            }),
        },
        electricalProjectOrganizationId: z.string().min(1),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean().nullable().optional(),
        isObjectOwner: z.boolean().nullable().optional(),
        isObjectAdministrator: z.boolean().nullable().optional(),
        isObjectTenant: z.boolean().nullable().optional(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectOrganizationId: z.string().min(1),
            name: z.string().max(100).nullable().optional(),
            description: z.string().max(250).nullable().optional(),
            email: z
                .union([z.literal(''), z.string().email()])
                .nullable()
                .optional(),
            telephoneNumber: z.string().max(60).nullable().optional(),
            website: z.string().max(250).nullable().optional(),
            isProjectCustomer: z.boolean().nullable().optional(),
            isObjectOwner: z.boolean().nullable().optional(),
            isObjectAdministrator: z.boolean().nullable().optional(),
            isObjectTenant: z.boolean().nullable().optional(),
            isPVInstallationConstructionCompany: z
                .boolean()
                .nullable()
                .optional(),
        }),
    },
    patchElectricalProjectPriceQuoteCommand: {
        electricalProjectVersionId: z.string().min(1),
        outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
        basePrice: z.number().min(0).max(100000).nullable().optional(),
        squareMeterCount: z.number().min(0).max(10000000).nullable().optional(),
        pricePerSquareMeter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pricePerDistributionBoard: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerControlBox: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRemoteRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        discountPercentage: z.number().min(0).max(25).nullable().optional(),
        pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVInverter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVPanel: z.number().min(0).max(10000000).nullable().optional(),
        priceQuoteDate: z.date().nullable().optional(),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
            basePrice: z.number().min(0).max(100000).nullable().optional(),
            squareMeterCount: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerSquareMeter: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            distributionBoardCount: z
                .number()
                .min(0)
                .max(10000)
                .nullable()
                .optional(),
            pricePerDistributionBoard: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerControlBox: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerRepeatInspection: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pricePerRemoteRepeatInspection: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            discountPercentage: z.number().min(0).max(25).nullable().optional(),
            pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerPVInverter: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
            pricePerPVPanel: z
                .number()
                .min(0)
                .max(10000000)
                .nullable()
                .optional(),
            priceQuoteDate: z.date().nullable().optional(),
        }),
    },
    patchElectricalProjectPVInverterCommand: {
        electricalPowerPhaseKindEnum: ['1', '3'] as const,
        electricalProjectPVInverterId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number().nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVInverterId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            internalRemark: z.string().max(500).nullable().optional(),
            internalImageRemark: z.string().max(500).nullable().optional(),
            internalThermalImageRemark: z
                .string()
                .max(500)
                .nullable()
                .optional(),
            brandModel: z.string().max(100).nullable().optional(),
            serialNumber: z.string().max(100).nullable().optional(),
            firmwareVersion: z.string().max(100).nullable().optional(),
            ratedPower: z.number().min(0).max(100000).nullable().optional(),
            powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
            order: z.number().nullable().optional(),
            electricalProjectDistributionBoardId: z
                .string()
                .nullable()
                .optional(),
            groundImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            groundImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL1: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            neutralImpedanceL3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L2: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL1L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            impedanceL2L3: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            reportImages: z.boolean().nullable().optional(),
            reportThermalImages: z.boolean().nullable().optional(),
            reportImpedanceMeasurements: z.boolean().nullable().optional(),
            reportPVStrings: z.boolean().nullable().optional(),
        }),
    },
    patchElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectPVStringId: z.string().min(1),
            identifier: z.string().max(100).nullable().optional(),
            voltage: z.number().min(0).max(100000).nullable().optional(),
            resistance: z.number().min(0).max(100000).nullable().optional(),
            current: z.number().min(0).max(100000).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    patchElectricalProjectRisoMeasurementCommand: {
        electricalRisoMeasurementCircuitKindEnum: [
            'L-PE / N-PE',
            'L1-L2',
            'L2-L3',
            'L1-L3',
            'L1-N',
            'L2-N',
            'L3-N',
            'L1-PE',
            'L2-PE',
            'L3-PE',
            'N-PE',
            'L-PE',
        ] as const,
        electricalProjectRisoMeasurementId: z.string().min(1),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectRisoMeasurementId: z.string().min(1),
            circuitKind: z
                .enum([
                    'L-PE / N-PE',
                    'L1-L2',
                    'L2-L3',
                    'L1-L3',
                    'L1-N',
                    'L2-N',
                    'L3-N',
                    'L1-PE',
                    'L2-PE',
                    'L3-PE',
                    'N-PE',
                    'L-PE',
                ])
                .nullable()
                .optional(),
            measuredValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            minimumValue: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            isFaulty: z.boolean().nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    patchElectricalProjectSelectListItemCommand: {
        patchElectricalProjectSelectListItemCommandItem: {
            electricalInspectionKindEnum: [
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ] as const,
            electricalProjectSelectListItemId: z.string().min(1),
            selected: z.boolean().nullable().optional(),
            addInspectionKind: z
                .enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
                .nullable()
                .optional(),
            customValue: z.string().max(500).nullable().optional(),
            bodySchema: z.object({
                electricalProjectSelectListItemId: z.string().min(1),
                selected: z.boolean().nullable().optional(),
                addInspectionKind: z
                    .enum([
                        'None',
                        'Scope8',
                        'Scope10',
                        'Scope12',
                        'Nen1010',
                        'Nen3140',
                    ])
                    .nullable()
                    .optional(),
                customValue: z.string().max(500).nullable().optional(),
            }),
        },
        electricalProjectSelectListKindEnum: [
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ] as const,
        electricalProjectVersionId: z.string().min(1),
        listKind: z.enum([
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ]),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
            listKind: z.enum([
                'InspectionExclusion',
                'ObjectSpecialSpace',
                'AdditionalInstruction',
                'AdditionalTopic',
                'InspectionComponent',
                'BuildingFunction',
            ]),
        }),
    },
    patchElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().min(1),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().max(200).nullable().optional(),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number().nullable().optional(),
        bodySchema: z.object({
            electricalProjectTechnicalDrawingId: z.string().min(1),
            isDrawingPresent: z.boolean().nullable().optional(),
            electricalProjectFileId: z.string().nullable().optional(),
            name: z.string().max(200).nullable().optional(),
            location: z.string().max(200).nullable().optional(),
            description: z.string().max(500).nullable().optional(),
            order: z.number().nullable().optional(),
        }),
    },
    publishElectricalProjectCommand: {
        electricalProjectVersionId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
        }),
    },
    removeRoleFromUserCommand: {
        userId: z.string().min(1),
        bodySchema: z.object({
            userId: z.string().min(1),
        }),
    },
    repairElectricalProjectObservationPortalCommand: {
        electricalProjectObservationId: z.string().min(1),
        isFixed: z.boolean(),
        bodySchema: z.object({
            electricalProjectObservationId: z.string().min(1),
            isFixed: z.boolean(),
        }),
    },
    resetAccountPasswordV2Request: {
        userId: z.string().min(1),
        resetCode: z.string().min(1).max(1024),
        password: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
        bodySchema: z.object({
            userId: z.string().min(1),
            resetCode: z.string().min(1).max(1024),
            password: z
                .string()
                .min(10)
                .max(256)
                .regex(/^.{10,256}$/),
        }),
    },
    revokeElectricalProjectCommand: {
        electricalProjectVersionId: z.string().min(1),
        bodySchema: z.object({
            electricalProjectVersionId: z.string().min(1),
        }),
    },
} as const;
